import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  ButtonGroup,
  Divider,
  Menu,
  MenuItem,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  SvgIcon,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { savePdfWithAnnotations } from "../services/PdfAnnotationService";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ShortTextIcon from "@mui/icons-material/ShortText";
import CropFreeIcon from "@mui/icons-material/CropFree";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LabelIcon from "@mui/icons-material/Label";
import FlagIcon from "@mui/icons-material/Flag";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";

/** I-beam cursor icon for text highlight / text selection mode */
const TextHighlightIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24" sx={{ fontSize: 20 }}>
    <path d="M12 4v16M10 4h4M10 20h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
  </SvgIcon>
);

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

const MIN_BOX_SIZE = 0.25;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/** @returns {{ pageNumber: number, rect: DOMRect, el: Element }[]} */
function collectPdfPageRects(containerEl) {
  if (!containerEl) return [];
  const nodes = containerEl.querySelectorAll("[data-page-number]");
  return Array.from(nodes)
    .map((el) => {
      const num = Number(el.getAttribute("data-page-number"));
      const rect = el.getBoundingClientRect();
      return { pageNumber: num, rect, el };
    })
    .filter(
      (p) =>
        Number.isFinite(p.pageNumber) &&
        p.pageNumber > 0 &&
        p.rect.width > 0 &&
        p.rect.height > 0,
    );
}

function findPageRectForPageNumber(containerEl, pageNumber) {
  return collectPdfPageRects(containerEl).find((p) => p.pageNumber === pageNumber) || null;
}

/** Page whose bounds contain the point (topmost in DOM order wins on overlap). */
function findPageUnderClientPoint(containerEl, clientX, clientY) {
  const pages = collectPdfPageRects(containerEl);
  for (let i = pages.length - 1; i >= 0; i--) {
    const { pageNumber, rect } = pages[i];
    if (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      return { pageNumber, rect };
    }
  }
  return null;
}

/** Prefer page with largest overlap with axis-aligned client rect (e.g. gutter between pages). */
function findBestPageForClientRect(containerEl, left, top, right, bottom) {
  const pages = collectPdfPageRects(containerEl);
  if (pages.length === 0) return null;
  let best = null;
  let bestArea = 0;
  for (const { pageNumber, rect } of pages) {
    const il = Math.max(left, rect.left);
    const it = Math.max(top, rect.top);
    const ir = Math.min(right, rect.right);
    const ib = Math.min(bottom, rect.bottom);
    const area = Math.max(0, ir - il) * Math.max(0, ib - it);
    if (area > bestArea) {
      bestArea = area;
      best = { pageNumber, rect };
    }
  }
  if (best && bestArea > 0) return best;
  const cx = (left + right) / 2;
  const cy = (top + bottom) / 2;
  return findPageUnderClientPoint(containerEl, cx, cy) || pages[0];
}

function percentLocationToClientRect(pageRect, loc) {
  const x = loc.x ?? 0;
  const y = loc.y ?? 0;
  const w = loc.width ?? 0;
  const h = loc.height ?? 0;
  const left = pageRect.left + (x / 100) * pageRect.width;
  const top = pageRect.top + (y / 100) * pageRect.height;
  const right = left + (w / 100) * pageRect.width;
  const bottom = top + (h / 100) * pageRect.height;
  return { left, top, right, bottom };
}

function clientRectToPercentLocation(pageRect, left, top, right, bottom) {
  let width = ((right - left) / pageRect.width) * 100;
  let height = ((bottom - top) / pageRect.height) * 100;
  width = clamp(width, MIN_BOX_SIZE, 100);
  height = clamp(height, MIN_BOX_SIZE, 100);
  let x = ((left - pageRect.left) / pageRect.width) * 100;
  let y = ((top - pageRect.top) / pageRect.height) * 100;
  x = clamp(x, 0, 100 - width);
  y = clamp(y, 0, 100 - height);
  return { x, y, width, height };
}

/** Rubber-band from start/end points on possibly different pages → one clamped rect on best page. */
function computeRubberBandPercentBox(container, startPage, startXY, endPage, endXY) {
  const sPg = findPageRectForPageNumber(container, startPage);
  const ePg = findPageRectForPageNumber(container, endPage);
  if (!sPg || !ePg) return null;
  const sx1 = sPg.rect.left + (startXY.x / 100) * sPg.rect.width;
  const sy1 = sPg.rect.top + (startXY.y / 100) * sPg.rect.height;
  const ex1 = ePg.rect.left + (endXY.x / 100) * ePg.rect.width;
  const ey1 = ePg.rect.top + (endXY.y / 100) * ePg.rect.height;
  const left = Math.min(sx1, ex1);
  const right = Math.max(sx1, ex1);
  const top = Math.min(sy1, ey1);
  const bottom = Math.max(sy1, ey1);
  const target = findBestPageForClientRect(container, left, top, right, bottom);
  if (!target) return null;
  const pct = clientRectToPercentLocation(target.rect, left, top, right, bottom);
  return { page: target.pageNumber, ...pct };
}

const resizeHandles = [
  { key: "nw", cursor: "nwse-resize", left: "-4px", top: "-4px" },
  { key: "n", cursor: "ns-resize", left: "calc(50% - 4px)", top: "-4px" },
  { key: "ne", cursor: "nesw-resize", right: "-4px", top: "-4px" },
  { key: "e", cursor: "ew-resize", right: "-4px", top: "calc(50% - 4px)" },
  { key: "se", cursor: "nwse-resize", right: "-4px", bottom: "-4px" },
  { key: "s", cursor: "ns-resize", left: "calc(50% - 4px)", bottom: "-4px" },
  { key: "sw", cursor: "nesw-resize", left: "-4px", bottom: "-4px" },
  { key: "w", cursor: "ew-resize", left: "-4px", top: "calc(50% - 4px)" },
];

const FieldMarker = ({
  field,
  isSelected,
  isGroupSelected,
  onSelect,
  onEditField,
  onAssignFieldToSection,
  identifySectionsMode,
  onStartMove,
  onStartResize,
  onHoverChange,
  isAnnotatorMode,
  isOptionMarker = false,
  optionLabel,
  selectId,
  markerMeta,
  legendIndex,
  hideResizeHandles = false,
  hideLabelAndIndex = false,
  glow = false,
  onRequestDelete,
}) => {
  const location = field.metadata?.pdfLocation;
  if (!location) return null;
  const hideLabels = hideLabelAndIndex || hideResizeHandles;

  return (
    <Tooltip
      title={
        hideLabels
          ? ""
          : isOptionMarker
            ? optionLabel
            : `${field.label} (${field.attributeName})`
      }
      disableHoverListener={hideLabels}
    >
      <Box
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          if (!isAnnotatorMode) {
            event.preventDefault();
          }
          event.stopPropagation();
          const id = selectId ?? field.id;
          const addToSelection = !!(event.ctrlKey || event.metaKey);
          // If this box is already part of the current selection (single or group) and the
          // user is not chord-clicking, keep the selection intact so the drag can move the
          // whole group. Otherwise replace/extend selection as before.
          const alreadyInSelection = isSelected || isGroupSelected;
          if (!(alreadyInSelection && !addToSelection)) {
            onSelect(id, { addToSelection });
          }
          onStartMove(event, field, markerMeta, { isInGroup: alreadyInSelection });
        }}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onDoubleClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (onRequestDelete) {
            onRequestDelete(field);
            return;
          }
          if (identifySectionsMode && onAssignFieldToSection) {
            onAssignFieldToSection(field);
            return;
          }
          onEditField(field);
        }}
        onMouseEnter={() => onHoverChange(field.id, markerMeta?.key ?? null)}
        onMouseLeave={() => onHoverChange(null, null)}
        sx={{
          position: "absolute",
          left: `${location.x}%`,
          top: `${location.y}%`,
          width: `${location.width}%`,
          height: `${location.height}%`,
          border: isSelected
            ? "2px solid #2e7d32"
            : isGroupSelected
            ? "2px solid #8e24aa"
            : field.touchedByJson
            ? "2px solid #00a896"
            : field.touchedByAnnotator
            ? "2px solid #f9a825"
            : isOptionMarker
            ? "2px solid #1e88e5"
            : "2px solid #388e3c",
          ...(glow && {
            boxShadow: "0 0 0 2px rgba(0, 168, 150, 0.4), 0 0 24px 8px rgba(0, 168, 150, 0.5)",
            zIndex: 100,
          }),
          backgroundColor: isGroupSelected
            ? "rgba(142, 36, 170, 0.25)"
            : field.touchedByJson
            ? "rgba(0, 168, 150, 0.2)"
            : field.touchedByAnnotator
            ? "rgba(249, 168, 37, 0.2)"
            : "rgba(46, 125, 50, 0.2)",
          cursor: "move",
          zIndex: field.excludeFromFormJSON ? 25 : 10,
          "&:hover": {
            backgroundColor: isGroupSelected
              ? "rgba(142, 36, 170, 0.4)"
              : "rgba(46, 125, 50, 0.4)",
            zIndex: field.excludeFromFormJSON ? 26 : 20,
          },
        }}
      >
        {legendIndex && !hideLabels ? (
          <Box
            sx={{
              position: "absolute",
              top: -12,
              left: -12,
              minWidth: 20,
              height: 20,
              px: 0.5,
              borderRadius: "999px",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontSize: "0.7rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 25,
              boxShadow: 1,
            }}
          >
            {legendIndex}
          </Box>
        ) : null}
        {!isAnnotatorMode && isGroupSelected && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: "-20px",
              right: 0,
              backgroundColor: "rgba(142, 36, 170, 0.9)",
              color: "white",
              padding: "2px 6px",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              zIndex: 20,
              fontSize: "0.7rem",
            }}
          >
            Selected
          </Typography>
        )}
        {!isOptionMarker && !hideLabels && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: "-20px",
              left: 0,
              backgroundColor: "rgba(0,0,0,0.8)",
              color: "white",
              padding: "2px 6px",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              zIndex: 20,
              fontSize: "0.75rem",
            }}
          >
            {field.attributeName || field.label || field.id}
          </Typography>
        )}
        {isSelected && !hideResizeHandles &&
          resizeHandles.map((handle) => (
            <Box
              key={handle.key}
              onPointerDown={(event) => {
                if (event.button !== 0) return;
                event.preventDefault();
                event.stopPropagation();
                onSelect(selectId ?? field.id);
                onStartResize(event, field, handle.key, markerMeta);
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              sx={{
                position: "absolute",
                width: "8px",
                height: "8px",
                backgroundColor: "white",
                border: "1px solid #2e7d32",
                borderRadius: "2px",
                cursor: handle.cursor,
                zIndex: 30,
                ...("left" in handle ? { left: handle.left } : {}),
                ...("right" in handle ? { right: handle.right } : {}),
                ...("top" in handle ? { top: handle.top } : {}),
                ...("bottom" in handle ? { bottom: handle.bottom } : {}),
              }}
            />
          ))}
        {isOptionMarker && !hideLabels && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: "-18px",
              left: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              color: "white",
              padding: "1px 4px",
              borderRadius: "3px",
              whiteSpace: "nowrap",
              zIndex: 20,
              fontSize: "0.7rem",
            }}
          >
            {optionLabel}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const SignatureMarker = ({
  annotation,
  isSelected,
  isGroupSelected = false,
  onSelect,
  onStartMove,
  onStartResize,
  onHoverChange,
  onRequestDelete,
  hideLabel = false,
}) => {
  const location = annotation?.pdfLocation;
  if (!location) return null;
  return (
    <Box
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        const addToSelection = !!(event.ctrlKey || event.metaKey);
        const alreadyInSelection = isSelected || isGroupSelected;
        if (!(alreadyInSelection && !addToSelection)) {
          onSelect(annotation.id, { addToSelection });
        }
        onStartMove(event, annotation, { isInGroup: alreadyInSelection });
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onDoubleClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (onRequestDelete) {
          onRequestDelete(annotation);
        }
      }}
      onMouseEnter={() => onHoverChange(annotation.id)}
      onMouseLeave={() => onHoverChange(null)}
      sx={{
        position: "absolute",
        left: `${location.x}%`,
        top: `${location.y}%`,
        width: `${location.width}%`,
        height: `${location.height}%`,
        border: isSelected ? "2px solid #f39c12" : "2px dashed #f39c12",
        backgroundColor: "rgba(243, 156, 18, 0.15)",
        cursor: "move",
        zIndex: 9,
        "&:hover": {
          backgroundColor: "rgba(243, 156, 18, 0.3)",
        },
      }}
    >
      {!hideLabel && (
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: "-20px",
            left: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            color: "white",
            padding: "2px 6px",
            borderRadius: "4px",
            whiteSpace: "nowrap",
            zIndex: 20,
            fontSize: "0.7rem",
          }}
        >
          {annotation.markerPrefix || "sig"}-{annotation.signerKey}
        </Typography>
      )}
      {isSelected &&
        resizeHandles.map((handle) => (
          <Box
            key={handle.key}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              event.preventDefault();
              event.stopPropagation();
              onSelect(annotation.id);
              onStartResize(event, annotation, handle.key);
            }}
            sx={{
              position: "absolute",
              width: "8px",
              height: "8px",
              backgroundColor: "white",
              border: "1px solid #f39c12",
              borderRadius: "2px",
              cursor: handle.cursor,
              ...handle,
            }}
          />
        ))}
    </Box>
  );
};

const LineHeightCaptureBox = ({ box, onStartMove, onStartResize }) => {
  if (!box || box.page == null) return null;
  const { x, y, width, height } = box;
  return (
    <Box
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        onStartMove(event, box);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        position: "absolute",
        left: `${x}%`,
        top: `${y}%`,
        width: `${width}%`,
        height: `${height}%`,
        border: "2px dashed",
        borderColor: "primary.main",
        backgroundColor: "rgba(0, 168, 150, 0.2)",
        cursor: "move",
        zIndex: 10,
        "&:hover": { backgroundColor: "rgba(0, 168, 150, 0.3)" },
      }}
    >
      <Typography variant="caption" sx={{ position: "absolute", top: "-18px", left: 0, color: "primary.main", fontSize: "0.7rem" }}>
        Line height
      </Typography>
      {resizeHandles.map((handle) => (
        <Box
          key={handle.key}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            onStartResize(event, box, handle.key);
          }}
          sx={{
            position: "absolute",
            width: "8px",
            height: "8px",
            backgroundColor: "white",
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: "2px",
            cursor: handle.cursor,
            ...handle,
          }}
        />
      ))}
    </Box>
  );
};

const buildOptionMarkers = (field) => {
  const markers = [];
  const metadata = field.metadata || {};
  const options = Array.isArray(metadata.options) ? metadata.options : [];

  options.forEach((option, index) => {
    if (option?.pdfLocation) {
      markers.push({
        key: `${field.id}-option-${index}`,
        kind: "option",
        index,
        location: option.pdfLocation,
        label: option.text || option.value || `Option ${index + 1}`,
      });
    }
  });

  if (metadata.other?.checkbox?.pdfLocation) {
    markers.push({
      key: `${field.id}-other-checkbox`,
      kind: "otherCheckbox",
      location: metadata.other.checkbox.pdfLocation,
      label: metadata.other.checkbox.text || "Other",
    });
  }

  if (metadata.other?.radio?.pdfLocation) {
    markers.push({
      key: `${field.id}-other-radio`,
      kind: "otherRadio",
      location: metadata.other.radio.pdfLocation,
      label: metadata.other.radio.text || "Other",
    });
  }

  if (metadata.other?.input?.pdfLocation) {
    markers.push({
      key: `${field.id}-other-input`,
      kind: "otherInput",
      location: metadata.other.input.pdfLocation,
      label: metadata.other.input.attributeName || "Other Input",
    });
  }

  return markers;
};

const InteractivePdfViewer = ({
  pdfFile,
  onClose,
  onReplacePdf,
  onPdfClick,
  fields = [],
  signatureAnnotations = [],
  initialsAnnotations = [],
  additionalFields = [],
  signatures = {},
  signerAttributeBindings = [],
  initialerAttributeBindings = [],
  subsequentSigningBindings = [],
  onEditField,
  onFieldLocationChange,
  onOptionLocationChange,
  onSignatureLocationChange,
  onDeleteSignature,
  onAdditionalFieldLocationChange,
  onDeleteAdditionalField,
  onSignerBindingLocationChange,
  onDeleteSignerBinding,
  onSubsequentSigningLocationChange,
  onDeleteSubsequentSigningBinding,
  onAddSignerBindingAnnotation,
  onAddSubsequentSigningAnnotation,
  onAddAdditionalAnnotation,
  identifySectionsMode = false,
  onToggleIdentifySections,
  signaturePlacementMode = false,
  initialsPlacementMode = false,
  onToggleSignaturePlacement,
  onToggleInitialsPlacement,
  onSelectFields,
  onAssignFieldToSection,
  selectedFieldIds = [],
  primarySelectId = null,
  onSignatureSelect,
  mode = "json",
  defaultLineHeightPx = null,
  preserveDrawnBoxSize = false,
  lineHeightSettingMode = false,
  lineHeightCaptureBox = null,
  onLineHeightSet = null,
  onLineHeightConfirm = null,
  onLineHeightCancel = null,
  onLineHeightReset = null,
  onLineHeightCaptureChange = null,
  onFieldSelect = null,
  scrollToFieldId = null,
  onScrollToFieldDone = null,
  annotatorMarkComplete = null,
  jsonMarkComplete = null,
  studioFormFlag = null,
  onLineHeightConfirmError = null,
  onInstantCreate = null,
}) => {
  const theme = useTheme();
  const mergedSignatureAnnotations = useMemo(
    () => [
      ...(signatureAnnotations || []).map((a) => ({ ...a, markerPrefix: "sig" })),
      ...(initialsAnnotations || []).map((a) => ({ ...a, markerPrefix: "init" })),
    ],
    [signatureAnnotations, initialsAnnotations],
  );
  const mergedSignerAttributeBindings = useMemo(
    () => [...(signerAttributeBindings || []), ...(initialerAttributeBindings || [])],
    [signerAttributeBindings, initialerAttributeBindings],
  );
  const isDarkMode = theme.palette.mode === "dark";
  const [numPages, setNumPages] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const dragStartRef = useRef(null);
  const dragEndRef = useRef(null);
  const dragSessionRef = useRef(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);

  const isControlledSelection = Array.isArray(selectedFieldIds);
  const handleSelectField = (id, opts = {}) => {
    if (isControlledSelection) {
      if (onFieldSelect) onFieldSelect(id, opts);
      return;
    }
    setSelectedFieldId(id);
    if (onFieldSelect) onFieldSelect(id, opts);
  };
  const primarySelectedId = isControlledSelection
    ? (primarySelectId != null ? primarySelectId : (selectedFieldIds?.[0] ?? null))
    : selectedFieldId;
  const [activeDrag, setActiveDrag] = useState(null);
  const [hoveredFieldId, setHoveredFieldId] = useState(null);
  const [hoveredMarkerKey, setHoveredMarkerKey] = useState(null);
  const [hoveredSignatureId, setHoveredSignatureId] = useState(null);
  const [scale, setScale] = useState(0.75);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [selectedPage, setSelectedPage] = useState(1);
  const [tintEnabled, setTintEnabled] = useState(true);
  const pdfContainerRef = useRef(null);
  const hasSetInitialFillScale = useRef(false);
  const [selectedSignatureId, setSelectedSignatureId] = useState(null);

  useEffect(() => {
    if (!isControlledSelection) return;
    /** Match primarySelectedId: fall back to first selected id when pdfPrimarySelectId is unset */
    const resolvedPrimary =
      primarySelectId != null ? primarySelectId : (selectedFieldIds?.[0] ?? null);
    if (!resolvedPrimary) {
      setSelectedSignatureId(null);
      return;
    }
    const isAnn = (mergedSignatureAnnotations || []).some((a) => a.id === resolvedPrimary);
    setSelectedSignatureId(isAnn ? resolvedPrimary : null);
  }, [isControlledSelection, primarySelectId, selectedFieldIds, mergedSignatureAnnotations]);

  const [toolbarOffset, setToolbarOffset] = useState({ x: 0, y: 0 });
  const toolbarDragRef = useRef(null);
  const signatureLocRef = useRef(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [hideResizeHandlesUntil, setHideResizeHandlesUntil] = useState(null);
  const [showAnnotationLabels, setShowAnnotationLabels] = useState(mode !== "annotator");
  const [textHighlightMode, setTextHighlightMode] = useState(false);
  const [glowFieldId, setGlowFieldId] = useState(null);
  const [pendingBox, setPendingBox] = useState(null); // { page, x, y, width, height, screenX, screenY } awaiting type pick
  const [pendingBoxType, setPendingBoxType] = useState(null); // null | "input" | "checkbox" | "decision" | "decisionWithOther" | "additional" | "persona" | "duplicate"
  const [checkboxOptions, setCheckboxOptions] = useState([]); // option labels for checkbox / decision / decisionWithOther pending step
  const [pendingAdditionalAttributeName, setPendingAdditionalAttributeName] = useState("");
  const [pendingAdditionalLines, setPendingAdditionalLines] = useState(0);
  const [pendingDuplicateAttributeName, setPendingDuplicateAttributeName] = useState("");
  const [pendingPersonaAttributeName, setPendingPersonaAttributeName] = useState("");
  const [pendingPersonaLines, setPendingPersonaLines] = useState(0);
  const [pendingPersonaRoleSignerKey, setPendingPersonaRoleSignerKey] = useState("");

  const signerOptionsFlat = useMemo(() => {
    const opts = [];
    Object.entries(signatures || {}).forEach(([roleKey, rv]) => {
      const roleLabel = rv?.label || roleKey;
      (rv?.signatures || []).forEach((sig) => {
        if (!sig?.signerKey) return;
        opts.push({
          compound: `${roleKey}\t${sig.signerKey}`,
          roleKey,
          signerKey: sig.signerKey,
          label: `${roleLabel}: ${sig.signerKey}${sig.email ? ` (${sig.email})` : ""}`,
        });
      });
    });
    return opts;
  }, [signatures]);

  const confirmLineHeightFromBox = useCallback(() => {
    if (!lineHeightCaptureBox || !onLineHeightConfirm) return;
    const pageEl = pdfContainerRef.current?.querySelector(
      `[data-page-number="${lineHeightCaptureBox.page}"]`
    );
    const pageRect = pageEl?.getBoundingClientRect();
    if (pageRect?.height) {
      const lineHeightPx = (lineHeightCaptureBox.height / 100) * pageRect.height;
      onLineHeightConfirm(Math.round(lineHeightPx));
      return;
    }
    const fallbackPx = Math.round((lineHeightCaptureBox.height / 100) * 1100);
    if (fallbackPx > 0 && fallbackPx < 200) {
      onLineHeightConfirm(fallbackPx);
    } else {
      onLineHeightConfirmError?.("Couldn't read page size. Scroll so the line-height box is in view and try Confirm again.");
    }
  }, [lineHeightCaptureBox, onLineHeightConfirm, onLineHeightConfirmError]);

  useEffect(() => {
    if (hideResizeHandlesUntil == null) return;
    const t = setTimeout(() => setHideResizeHandlesUntil(null), 1500);
    return () => clearTimeout(t);
  }, [hideResizeHandlesUntil]);

  const hideResizeHandles = hideResizeHandlesUntil != null;
  const hideLabelAndIndex = !showAnnotationLabels || hideResizeHandles || activeDrag != null;

  /** Section fields with a linked extra-PDF binding show only the binding marker, not the main field box. */
  const fieldIdsWithLinkedExtraPdfBinding = useMemo(() => {
    const ids = new Set();
    // Ownership only. A row merely sharing a field's attributeName is an independent placement,
    // and must not hide the field's own box — see utils/additionalFieldLink.js.
    const collect = (rows) => {
      (rows || []).forEach((row) => {
        if (row?.linkedFieldId) ids.add(row.linkedFieldId);
      });
    };
    collect(additionalFields);
    collect(signerAttributeBindings);
    collect(initialerAttributeBindings);
    collect(signatureAnnotations);
    collect(initialsAnnotations);
    collect(subsequentSigningBindings);
    return ids;
  }, [
    additionalFields,
    signerAttributeBindings,
    initialerAttributeBindings,
    signatureAnnotations,
    initialsAnnotations,
    subsequentSigningBindings,
  ]);

  const shouldRenderSectionFieldMainMarker = useCallback(
    (field) => {
      if (!field?.metadata?.pdfLocation?.page) return false;
      if (field.excludeFromFormJSON) return false;
      if (fieldIdsWithLinkedExtraPdfBinding.has(field.id)) return false;
      return true;
    },
    [fieldIdsWithLinkedExtraPdfBinding],
  );

  const legendItems = useMemo(() => {
    const items = [];
    fields.forEach((field) => {
      const location = field.metadata?.pdfLocation;
      if (location?.page && shouldRenderSectionFieldMainMarker(field)) {
        items.push({
          key: field.id,
          label: field.label || field.attributeName || field.id,
          page: location.page,
          x: location.x,
          y: location.y,
          width: location.width,
          height: location.height,
          kind: "field",
        });
      }
      buildOptionMarkers(field).forEach((marker) => {
        if (!marker.location?.page) return;
        items.push({
          key: marker.key,
          label: marker.label,
          page: marker.location.page,
          x: marker.location.x,
          y: marker.location.y,
          width: marker.location.width,
          height: marker.location.height,
          kind: "option",
        });
      });
    });
    (additionalFields || []).forEach((af) => {
      const loc = af.pdfLocation;
      if (!loc?.page) return;
      items.push({
        key: af.id,
        label: af.attributeName || "Additional",
        page: loc.page,
        x: loc.x,
        y: loc.y,
        width: loc.width,
        height: loc.height,
        kind: "additional",
      });
    });
    (mergedSignerAttributeBindings || []).forEach((sb) => {
      const loc = sb.pdfLocation;
      if (!loc?.page) return;
      items.push({
        key: sb.id,
        label: sb.attributeName ? `${sb.attributeName} (${sb.signerKey})` : sb.signerKey,
        page: loc.page,
        x: loc.x,
        y: loc.y,
        width: loc.width,
        height: loc.height,
        kind: "signerBinding",
      });
    });
    (subsequentSigningBindings || []).forEach((ss) => {
      const loc = ss.pdfLocation;
      if (!loc?.page) return;
      const n = (ss.signers || []).length;
      items.push({
        key: ss.id,
        label: ss.attributeName ? `${ss.attributeName} (subsequent${n ? ` ×${n}` : ""})` : "Subsequent signing",
        page: loc.page,
        x: loc.x,
        y: loc.y,
        width: loc.width,
        height: loc.height,
        kind: "subsequentSigning",
      });
    });
    return items.sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x);
  }, [fields, additionalFields, mergedSignerAttributeBindings, subsequentSigningBindings, shouldRenderSectionFieldMainMarker]);

  const legendIndexByKey = useMemo(() => {
    const map = new Map();
    legendItems.forEach((item, index) => {
      map.set(item.key, index + 1);
    });
    return map;
  }, [legendItems]);

  const selectedNudgeTarget = useMemo(() => {
    if (!primarySelectedId) return null;
    // Resolve option markers first so nudging/resizing always affects the selected option, not the parent field
    for (const f of fields) {
      const markers = buildOptionMarkers(f);
      const marker = markers.find((m) => m.key === primarySelectedId);
      if (marker?.location) {
        return { type: "option", field: f, location: marker.location, page: marker.location.page, markerMeta: marker };
      }
    }
    const field = fields.find((f) => f.id === primarySelectedId);
    if (field?.metadata?.pdfLocation) {
      return { type: "field", field, location: field.metadata.pdfLocation, page: field.metadata.pdfLocation.page };
    }
    const sb = (mergedSignerAttributeBindings || []).find((s) => s.id === primarySelectedId);
    if (sb?.pdfLocation) {
      return { type: "signerBinding", id: sb.id, location: sb.pdfLocation, page: sb.pdfLocation.page };
    }
    const ss = (subsequentSigningBindings || []).find((s) => s.id === primarySelectedId);
    if (ss?.pdfLocation) {
      return {
        type: "subsequentSigning",
        id: ss.id,
        location: ss.pdfLocation,
        page: ss.pdfLocation.page,
      };
    }
    const af = (additionalFields || []).find((a) => a.id === primarySelectedId);
    if (af?.pdfLocation) {
      return { type: "additional", id: af.id, location: af.pdfLocation, page: af.pdfLocation.page };
    }
    const sigAnn = (mergedSignatureAnnotations || []).find((a) => a.id === primarySelectedId);
    if (sigAnn?.pdfLocation) {
      return {
        type: "signatureAnnotation",
        id: sigAnn.id,
        location: sigAnn.pdfLocation,
        page: sigAnn.pdfLocation.page,
      };
    }
    return null;
  }, [primarySelectedId, fields, additionalFields, mergedSignerAttributeBindings, subsequentSigningBindings, mergedSignatureAnnotations]);

  // Expand a single selected id to ALL its movable PDF targets. A field id with options expands to its
  // main location (if any) plus every option marker with a pdfLocation. An option-marker key expands to
  // just that one option. Used for group drag/nudge so checkbox/radio fields whose parent has no main
  // pdfLocation still contribute their option boxes to the group move.
  const expandSelectionToTargets = (id) => {
    if (!id) return [];
    for (const f of fields) {
      const markers = buildOptionMarkers(f);
      const marker = markers.find((m) => m.key === id);
      if (marker?.location) {
        return [{ kind: "option", parentFieldId: f.id, markerMeta: marker, location: marker.location }];
      }
    }
    const field = fields.find((f) => f.id === id);
    if (field) {
      const out = [];
      if (field.metadata?.pdfLocation) {
        out.push({ kind: "field", id: field.id, location: field.metadata.pdfLocation });
      }
      buildOptionMarkers(field).forEach((marker) => {
        if (marker?.location) {
          out.push({ kind: "option", parentFieldId: field.id, markerMeta: marker, location: marker.location });
        }
      });
      return out;
    }
    const sb = (mergedSignerAttributeBindings || []).find((s) => s.id === id);
    if (sb?.pdfLocation) return [{ kind: "signerBinding", id: sb.id, location: sb.pdfLocation }];
    const ss = (subsequentSigningBindings || []).find((s) => s.id === id);
    if (ss?.pdfLocation) return [{ kind: "subsequentSigning", id: ss.id, location: ss.pdfLocation }];
    const af = (additionalFields || []).find((a) => a.id === id);
    if (af?.pdfLocation) return [{ kind: "additional", id: af.id, location: af.pdfLocation }];
    const sigAnn = (mergedSignatureAnnotations || []).find((a) => a.id === id);
    if (sigAnn?.pdfLocation) return [{ kind: "signatureAnnotation", id: sigAnn.id, location: sigAnn.pdfLocation }];
    return [];
  };

  // Returns true when the resolved target represents the drag's primary (so we don't move it twice).
  const isPrimaryTarget = (t, primaryId) => {
    if (!t || !primaryId) return false;
    if (t.kind === "option") return t.markerMeta?.key === primaryId;
    if (t.id) return t.id === primaryId;
    return false;
  };

  const emitTargetLocation = (t, next) => {
    switch (t.kind) {
      case "field":
        if (onFieldLocationChange) onFieldLocationChange(t.id, next);
        break;
      case "option":
        if (onOptionLocationChange) onOptionLocationChange(t.parentFieldId, t.markerMeta, next);
        break;
      case "additional":
        if (onAdditionalFieldLocationChange) onAdditionalFieldLocationChange(t.id, next);
        break;
      case "signerBinding":
        if (onSignerBindingLocationChange) onSignerBindingLocationChange(t.id, next);
        break;
      case "subsequentSigning":
        if (onSubsequentSigningLocationChange) onSubsequentSigningLocationChange(t.id, next);
        break;
      case "signatureAnnotation":
        if (onSignatureLocationChange) onSignatureLocationChange(t.id, next);
        break;
      default:
        break;
    }
  };

  // Translate one resolved selection target by (dxPct, dyPct) on its current page (keyboard nudge).
  // `startLocation` (optional) is the snapshot at drag-start; falls back to the current location.
  const applyGroupDeltaToTarget = (t, dxPct, dyPct, startLocation) => {
    const base = startLocation || t.location;
    if (!base) return;
    const next = {
      ...base,
      x: Math.max(0, Math.min(100 - base.width, base.x + dxPct)),
      y: Math.max(0, Math.min(100 - base.height, base.y + dyPct)),
    };
    emitTargetLocation(t, next);
  };

  // Translate by pixel delta with cross-page promotion (group drag companions).
  const applyGroupPixelDeltaToTarget = (container, t, dPxX, dPxY, startLocation) => {
    const base = startLocation || t.location;
    if (!base || !container) return;
    const startPageNumber = base.page ?? null;
    if (startPageNumber == null) return;
    const startPageEntry = findPageRectForPageNumber(container, startPageNumber);
    const startPageRect = startPageEntry?.rect;
    if (!startPageRect) return;

    const startClient = percentLocationToClientRect(startPageRect, base);
    const newLeft = startClient.left + dPxX;
    const newTop = startClient.top + dPxY;
    const newRight = startClient.right + dPxX;
    const newBottom = startClient.bottom + dPxY;
    const targetPage =
      findBestPageForClientRect(container, newLeft, newTop, newRight, newBottom) ||
      startPageEntry || { pageNumber: startPageNumber, rect: startPageRect };
    const pct = clientRectToPercentLocation(targetPage.rect, newLeft, newTop, newRight, newBottom);
    emitTargetLocation(t, {
      ...base,
      page: targetPage.pageNumber,
      ...pct,
    });
  };

  // Snapshot companions for group drag. Expands each selected id to ALL its movable parts and excludes
  // the primary being dragged (so it isn't moved twice — once by the primary emit, once as a companion).
  const collectCompanions = (primaryId, { isInGroup } = {}) => {
    if (!isInGroup) return [];
    if (!isControlledSelection || !Array.isArray(selectedFieldIds) || selectedFieldIds.length < 1) return [];
    const companions = [];
    for (const id of selectedFieldIds) {
      const targets = expandSelectionToTargets(id);
      for (const t of targets) {
        if (isPrimaryTarget(t, primaryId)) continue;
        if (!t.location) continue;
        companions.push({ ...t, startLocation: t.location });
      }
    }
    return companions;
  };

  const applyNudge = (dxPx, dyPx, dwPx, dhPx) => {
    // Group translate: 2+ selected and a pure move (no resize) → shift every selected item by the same pixel delta,
    // converted to a percent on each item's own page so 1px arrow nudge stays 1px on every page.
    if (
      isControlledSelection &&
      Array.isArray(selectedFieldIds) &&
      selectedFieldIds.length > 1 &&
      dwPx === 0 && dhPx === 0 &&
      (dxPx !== 0 || dyPx !== 0)
    ) {
      const container = pdfContainerRef.current;
      if (container) {
        for (const sid of selectedFieldIds) {
          const targets = expandSelectionToTargets(sid);
          for (const t of targets) {
            if (!t.location) continue;
            const pageEl = container.querySelector(`[data-page-number="${t.location.page}"]`);
            const rect = pageEl?.getBoundingClientRect();
            if (!rect?.width || !rect?.height) continue;
            const dxPct = (dxPx / rect.width) * 100;
            const dyPct = (dyPx / rect.height) * 100;
            applyGroupDeltaToTarget(t, dxPct, dyPct);
          }
        }
        setHideResizeHandlesUntil(Date.now() + 1500);
      }
      return;
    }

    const target = selectedNudgeTarget;
    if (!target) return;
    if (target.type === "signerBinding") {
      if (!onSignerBindingLocationChange) return;
      const pageEl = pdfContainerRef.current?.querySelector(`[data-page-number="${target.page}"]`);
      const rect = pageEl?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return;
      const dx = (dxPx / rect.width) * 100;
      const dy = (dyPx / rect.height) * 100;
      const dw = (dwPx / rect.width) * 100;
      const dh = (dhPx / rect.height) * 100;
      const loc = target.location;
      const w = Math.max(MIN_BOX_SIZE, Math.min(100, loc.width + dw));
      const h = Math.max(MIN_BOX_SIZE, Math.min(100, loc.height + dh));
      const next = {
        ...loc,
        x: Math.max(0, Math.min(100 - w, loc.x + dx)),
        y: Math.max(0, Math.min(100 - h, loc.y + dy)),
        width: w,
        height: h,
      };
      setHideResizeHandlesUntil(Date.now() + 1500);
      onSignerBindingLocationChange(target.id, next);
      return;
    }
    if (target.type === "signatureAnnotation") {
      if (!onSignatureLocationChange) return;
      const pageEl = pdfContainerRef.current?.querySelector(`[data-page-number="${target.page}"]`);
      const rect = pageEl?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return;
      const dx = (dxPx / rect.width) * 100;
      const dy = (dyPx / rect.height) * 100;
      const dw = (dwPx / rect.width) * 100;
      const dh = (dhPx / rect.height) * 100;
      const loc = target.location;
      const w = Math.max(MIN_BOX_SIZE, Math.min(100, loc.width + dw));
      const h = Math.max(MIN_BOX_SIZE, Math.min(100, loc.height + dh));
      const next = {
        ...loc,
        x: Math.max(0, Math.min(100 - w, loc.x + dx)),
        y: Math.max(0, Math.min(100 - h, loc.y + dy)),
        width: w,
        height: h,
      };
      setHideResizeHandlesUntil(Date.now() + 1500);
      onSignatureLocationChange(target.id, next);
      return;
    }
    if (target.type === "additional") {
      if (!onAdditionalFieldLocationChange) return;
      const pageEl = pdfContainerRef.current?.querySelector(`[data-page-number="${target.page}"]`);
      const rect = pageEl?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return;
      const dx = (dxPx / rect.width) * 100;
      const dy = (dyPx / rect.height) * 100;
      const dw = (dwPx / rect.width) * 100;
      const dh = (dhPx / rect.height) * 100;
      const loc = target.location;
      const w = Math.max(MIN_BOX_SIZE, Math.min(100, loc.width + dw));
      const h = Math.max(MIN_BOX_SIZE, Math.min(100, loc.height + dh));
      const next = {
        ...loc,
        x: Math.max(0, Math.min(100 - w, loc.x + dx)),
        y: Math.max(0, Math.min(100 - h, loc.y + dy)),
        width: w,
        height: h,
      };
      setHideResizeHandlesUntil(Date.now() + 1500);
      onAdditionalFieldLocationChange(target.id, next);
      return;
    }
    if (target.type === "subsequentSigning") {
      if (!onSubsequentSigningLocationChange) return;
      const pageEl = pdfContainerRef.current?.querySelector(`[data-page-number="${target.page}"]`);
      const rect = pageEl?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return;
      const dx = (dxPx / rect.width) * 100;
      const dy = (dyPx / rect.height) * 100;
      const dw = (dwPx / rect.width) * 100;
      const dh = (dhPx / rect.height) * 100;
      const loc = target.location;
      const w = Math.max(MIN_BOX_SIZE, Math.min(100, loc.width + dw));
      const h = Math.max(MIN_BOX_SIZE, Math.min(100, loc.height + dh));
      const next = {
        ...loc,
        x: Math.max(0, Math.min(100 - w, loc.x + dx)),
        y: Math.max(0, Math.min(100 - h, loc.y + dy)),
        width: w,
        height: h,
      };
      setHideResizeHandlesUntil(Date.now() + 1500);
      onSubsequentSigningLocationChange(target.id, next);
      return;
    }
    if (!onFieldLocationChange && !onOptionLocationChange) return;
    const pageEl = pdfContainerRef.current?.querySelector(`[data-page-number="${target.page}"]`);
    const rect = pageEl?.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return;
    const dx = (dxPx / rect.width) * 100;
    const dy = (dyPx / rect.height) * 100;
    const dw = (dwPx / rect.width) * 100;
    const dh = (dhPx / rect.height) * 100;
    const loc = target.location;
    const w = Math.max(MIN_BOX_SIZE, Math.min(100, loc.width + dw));
    const h = Math.max(MIN_BOX_SIZE, Math.min(100, loc.height + dh));
    const next = {
      ...loc,
      x: Math.max(0, Math.min(100 - w, loc.x + dx)),
      y: Math.max(0, Math.min(100 - h, loc.y + dy)),
      width: w,
      height: h,
    };
    setHideResizeHandlesUntil(Date.now() + 1500);
    if (target.type === "option" && onOptionLocationChange && target.markerMeta) {
      onOptionLocationChange(target.field.id, target.markerMeta, next);
    } else if (onFieldLocationChange) {
      onFieldLocationChange(target.field.id, next);
    }
  };

  // Keyboard shortcuts: Arrow = nudge, Alt+Arrow = resize (only when a field/option/additional is selected and focus not in an input)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const hasGroupMove =
        isControlledSelection &&
        Array.isArray(selectedFieldIds) &&
        selectedFieldIds.length > 1 &&
        !e.altKey;
      if (!selectedNudgeTarget && !hasGroupMove) return;
      if (!hasGroupMove) {
        /** Signatures use applySignatureNudge + signatureLocRef (below); avoid double-apply and stale locations */
        if (selectedNudgeTarget.type === "signatureAnnotation") return;
        if (selectedNudgeTarget.type === "signerBinding" && !onSignerBindingLocationChange) return;
        if (selectedNudgeTarget.type === "additional" && !onAdditionalFieldLocationChange) return;
        if (selectedNudgeTarget.type === "subsequentSigning" && !onSubsequentSigningLocationChange) return;
        if (
          selectedNudgeTarget.type !== "additional" &&
          selectedNudgeTarget.type !== "subsequentSigning" &&
          selectedNudgeTarget.type !== "signerBinding" &&
          selectedNudgeTarget.type !== "signatureAnnotation" &&
          !onFieldLocationChange &&
          !onOptionLocationChange
        )
          return;
      }
      const tag = document.activeElement?.tagName?.toLowerCase();
      const isInput = tag === "input" || tag === "textarea" || document.activeElement?.getAttribute?.("contenteditable") === "true";
      if (isInput) return;
      const isAlt = e.altKey;
      let dx = 0, dy = 0, dw = 0, dh = 0;
      switch (e.key) {
        case "ArrowLeft":  if (isAlt) dw = -1; else dx = -1; break;
        case "ArrowRight": if (isAlt) dw = 1;  else dx = 1;  break;
        case "ArrowUp":    if (isAlt) dh = -1; else dy = -1; break;
        case "ArrowDown":  if (isAlt) dh = 1;  else dy = 1;  break;
        default: return;
      }
      e.preventDefault();
      applyNudge(dx, dy, dw, dh);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedNudgeTarget,
    isControlledSelection,
    selectedFieldIds,
    onFieldLocationChange,
    onOptionLocationChange,
    onAdditionalFieldLocationChange,
    onSignerBindingLocationChange,
    onSubsequentSigningLocationChange,
    onSignatureLocationChange,
    applyNudge,
  ]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  // Reset so next PDF gets initial fill-scale
  useEffect(() => {
    hasSetInitialFillScale.current = false;
  }, [pdfFile]);

  // Default zoom: fill the viewbox width when PDF and container are ready (once per PDF)
  useEffect(() => {
    if (numPages == null || numPages < 1) return;
    const container = pdfContainerRef.current;
    if (!container) return;

    const setScaleToFillWidth = () => {
      if (hasSetInitialFillScale.current) return;
      const width = container.clientWidth;
      if (width <= 0) return;
      const padding = 32;
      const fillScale = Math.min(3, Math.max(0.5, (width - padding) / 900));
      setScale(fillScale);
      hasSetInitialFillScale.current = true;
    };

    setScaleToFillWidth();
    if (hasSetInitialFillScale.current) {
      return undefined;
    }

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (hasSetInitialFillScale.current) {
          ro.disconnect();
          return;
        }
        setScaleToFillWidth();
        if (hasSetInitialFillScale.current) ro.disconnect();
      });
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [numPages]);

  // ... (Handlers handleZoomIn, handleZoomOut, handleResetZoom, scrollToPage, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave, getSelectionBoxStyle remain the same)

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setScale(1.0);
  };

  const scrollToPage = (pageNumber) => {
    setSelectedPage(pageNumber);
    if (pdfContainerRef.current) {
      const container = pdfContainerRef.current;
      const pageElement = container.querySelector(
        `[data-page-number="${pageNumber}"]`
      );

      if (pageElement) {
        // Calculate position relative to container
        const containerRect = container.getBoundingClientRect();
        const elementRect = pageElement.getBoundingClientRect();

        // Current scroll + distance from container top + padding/margin buffer
        const scrollPosition =
          container.scrollTop + (elementRect.top - containerRect.top) - 20;

        container.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    }
  };

  const scrollToAnnotation = (item) => {
    if (!pdfContainerRef.current) return;
    const container = pdfContainerRef.current;
    const pageElement = container.querySelector(
      `[data-page-number="${item.page}"]`
    );
    if (!pageElement) return;
    const pageHeight = pageElement.offsetHeight;
    const pageOffset = pageElement.offsetTop;
    const annotationTop = pageOffset + (item.y / 100) * pageHeight;
    const annotationHeight = ((item.height ?? 0) / 100) * pageHeight;
    const annotationCenter = annotationTop + annotationHeight / 2;
    const viewportHalf = container.clientHeight / 2;
    let top = annotationCenter - viewportHalf;
    top = Math.max(0, Math.min(top, container.scrollHeight - container.clientHeight));
    container.scrollTo({ top, behavior: "smooth" });
    setSelectedPage(item.page);
  };

  // When parent requests scroll to a field (e.g. from Form Structure selection), scroll PDF to that annotation and show brief glow
  useEffect(() => {
    if (!scrollToFieldId || !fields?.length) return;
    const field = fields.find((f) => f.id === scrollToFieldId);
    const loc = field?.metadata?.pdfLocation;
    if (!loc?.page) {
      onScrollToFieldDone?.();
      return;
    }
    scrollToAnnotation({
      key: field.id,
      label: field.label || field.attributeName || field.id,
      page: loc.page,
      x: loc.x,
      y: loc.y,
      width: loc.width,
      height: loc.height,
      kind: "field",
    });
    setGlowFieldId(field.id);
    const t = setTimeout(() => setGlowFieldId(null), 2000);
    onScrollToFieldDone?.();
    return () => clearTimeout(t);
  }, [scrollToFieldId, fields]);

  const handleMouseDown = (event, pageNumber) => {
    if (textHighlightMode) return;
    if (pendingBox) {
      setPendingBox(null);
      setPendingBoxType(null);
      setCheckboxOptions([]);
      setDragStart(null);
      setDragEnd(null);
      setCurrentPage(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    dragSessionRef.current = { startPage: pageNumber };
    dragStartRef.current = { x, y };
    dragEndRef.current = { page: pageNumber, x, y };
    setIsDragging(true);
    setDragStart({ x, y });
    setDragEnd({ page: pageNumber, x, y });
    setCurrentPage(pageNumber);
  };

  const handleMouseMove = () => {
    /* cross-page drag: updates via window pointermove while isDragging */
  };

  const getHeightToUseForNewBox = (boxHeight, pageNum) => {
    if (preserveDrawnBoxSize) {
      return boxHeight;
    }
    if ((mode === "annotator" || mode === "json") && defaultLineHeightPx != null) {
      const pageEl = pdfContainerRef.current?.querySelector(
        `[data-page-number="${pageNum}"]`
      );
      const pageRect = pageEl?.getBoundingClientRect();
      if (pageRect?.height) {
        return (defaultLineHeightPx / pageRect.height) * 100;
      }
    }
    return boxHeight;
  };

  const applyRubberBandCompletionRef = useRef(null);
  applyRubberBandCompletionRef.current = (pageNumber, boxX, boxY, boxWidth, boxHeight, event) => {
    const selectionEnabled =
      (mode === "annotator" && identifySectionsMode) ||
      (mode === "json" && identifySectionsMode && !!onSelectFields);
    // Marquee multi-select runs in both JSON and Annotator modes, except when a placement-mode is active
    // (line-height capture, signature/initials placement, identifySections has its own selection branch above).
    const jsonBoxSelectEnabled =
      isControlledSelection &&
      onFieldSelect &&
      !identifySectionsMode &&
      !lineHeightSettingMode &&
      !signaturePlacementMode &&
      !initialsPlacementMode;

    const minDrawTriggerSize = preserveDrawnBoxSize ? MIN_BOX_SIZE : 1;

    if (lineHeightSettingMode && (boxWidth > 1 || boxHeight > 1) && onLineHeightCaptureChange) {
      onLineHeightCaptureChange({ page: pageNumber, x: boxX, y: boxY, width: boxWidth, height: boxHeight });
      return false;
    }

    if (selectionEnabled) {
      if (boxWidth > 1 || boxHeight > 1) {
        const selection = {
          x: boxX,
          y: boxY,
          width: boxWidth,
          height: boxHeight,
        };
        const selectedIds = fields
          .filter((field) => field.metadata?.pdfLocation?.page === pageNumber)
          .filter((field) => {
            const location = field.metadata?.pdfLocation;
            if (!location) return false;
            const left = location.x;
            const top = location.y;
            const right = location.x + location.width;
            const bottom = location.y + location.height;
            const selectionRight = selection.x + selection.width;
            const selectionBottom = selection.y + selection.height;
            const intersects =
              selection.x <= right &&
              selectionRight >= left &&
              selection.y <= bottom &&
              selectionBottom >= top;
            return intersects;
          })
          .map((field) => field.id);
        if (onSelectFields) {
          onSelectFields(selectedIds);
        }
      } else if (onSelectFields) {
        onSelectFields([]);
      }
      return false;
    }

    if (jsonBoxSelectEnabled && (boxWidth > 1 || boxHeight > 1)) {
      const selection = {
        x: boxX,
        y: boxY,
        width: boxWidth,
        height: boxHeight,
      };
      const selectionRight = selection.x + selection.width;
      const selectionBottom = selection.y + selection.height;
      // Marquee uses one page’s percent rect (pageNumber); option markers on other pages are not selected here.
      const boxIntersects = (loc) =>
        loc &&
        loc.page === pageNumber &&
        selection.x <= loc.x + loc.width &&
        selectionRight >= loc.x &&
        selection.y <= loc.y + loc.height &&
        selectionBottom >= loc.y;
      const selectedIdSet = new Set();
      fields.forEach((field) => {
        const mainLoc = field.metadata?.pdfLocation;
        if (boxIntersects(mainLoc)) selectedIdSet.add(field.id);
        buildOptionMarkers(field).forEach((marker) => {
          if (boxIntersects(marker.location)) selectedIdSet.add(marker.key);
        });
      });
      const selectedIds = [...selectedIdSet];
      if (selectedIds.length > 0) {
        onFieldSelect(selectedIds, { replaceWithMultiple: true });
        return false;
      }
      // Empty marquee falls through to the default draw-to-create branch below so that:
      //  - JSON mode: onPdfClick fires (same as before)
      //  - Annotator mode: setPendingBox opens the "create new field" popup
    }

    if (boxWidth > minDrawTriggerSize || boxHeight > minDrawTriggerSize) {
      if (onPdfClick) {
        const heightToUse = getHeightToUseForNewBox(boxHeight, pageNumber);
        if (
          mode === "annotator" &&
          !identifySectionsMode &&
          !lineHeightSettingMode &&
          !preserveDrawnBoxSize &&
          !signaturePlacementMode &&
          !initialsPlacementMode
        ) {
          setPendingBox({
            page: pageNumber,
            x: boxX,
            y: boxY,
            width: boxWidth,
            height: heightToUse,
            screenX: event.clientX,
            screenY: event.clientY,
          });
          return true;
        }
        onPdfClick(pageNumber, boxX, boxY, boxWidth, heightToUse);
      }
    }

    return false;
  };

  useEffect(() => {
    if (!isDragging) return undefined;
    const onMove = (e) => {
      const container = pdfContainerRef.current;
      if (!container) return;
      const hit = findPageUnderClientPoint(container, e.clientX, e.clientY);
      if (!hit) return;
      const x = ((e.clientX - hit.rect.left) / hit.rect.width) * 100;
      const y = ((e.clientY - hit.rect.top) / hit.rect.height) * 100;
      const next = { page: hit.pageNumber, x, y };
      dragEndRef.current = next;
      setDragEnd(next);
    };
    const onUp = (e) => {
      const sess = dragSessionRef.current;
      if (!sess) return;
      dragSessionRef.current = null;
      const container = pdfContainerRef.current;
      const startXY = dragStartRef.current;
      const end = dragEndRef.current;
      if (!container || !startXY) {
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        setCurrentPage(null);
        return;
      }
      const endPt = end || { page: sess.startPage, x: startXY.x, y: startXY.y };
      const band = computeRubberBandPercentBox(
        container,
        sess.startPage,
        startXY,
        endPt.page,
        { x: endPt.x, y: endPt.y },
      );
      if (!band) {
        setIsDragging(false);
        setDragStart(null);
        setDragEnd(null);
        setCurrentPage(null);
        return;
      }
      const { page: pageNumber, x: boxX, y: boxY, width: boxWidth, height: boxHeight } = band;
      const keepPreview = applyRubberBandCompletionRef.current(
        pageNumber,
        boxX,
        boxY,
        boxWidth,
        boxHeight,
        e,
      );
      setIsDragging(false);
      if (!keepPreview) {
        setDragStart(null);
        setDragEnd(null);
        setCurrentPage(null);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, true);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp, true);
    };
  }, [isDragging]);

  const handleMouseUp = () => {
    /* completion handled on window pointerup */
  };

  const handleMouseLeave = () => {
    /* do not cancel drag when pointer moves to another page */
  };

  useEffect(() => {
    if (!activeDrag) return;
    if (
      !onFieldLocationChange &&
      !onOptionLocationChange &&
      !onSignatureLocationChange &&
      !onAdditionalFieldLocationChange &&
      !onSignerBindingLocationChange &&
      !onSubsequentSigningLocationChange
    )
      return;

    const handlePointerMove = (event) => {
      const {
        startX,
        startY,
        startLocation,
        pageRect,
        mode,
        handle,
        fieldId,
        target,
        markerMeta,
      } = activeDrag;
      const dPxX = event.clientX - startX;
      const dPxY = event.clientY - startY;
      const container = pdfContainerRef.current;

      const startPageNumber =
        startLocation.page ?? activeDrag.lineHeightPage ?? null;
      const startPageEntry =
        container && startPageNumber != null
          ? findPageRectForPageNumber(container, startPageNumber)
          : null;
      const startPageRectResolved = startPageEntry?.rect || pageRect;

      const emit = (nextLocation, extra) => {
        const loc = extra ? { ...nextLocation, ...extra } : nextLocation;
        if (target === "lineHeightCapture" && activeDrag.lineHeightPage != null && onLineHeightCaptureChange) {
          onLineHeightCaptureChange({ ...loc, page: loc.page ?? activeDrag.lineHeightPage });
        } else if (target === "option" && onOptionLocationChange && markerMeta) {
          onOptionLocationChange(fieldId, markerMeta, loc);
        } else if (target === "signature" && onSignatureLocationChange) {
          onSignatureLocationChange(fieldId, loc);
        } else if (target === "additional" && onAdditionalFieldLocationChange) {
          onAdditionalFieldLocationChange(fieldId, loc);
        } else if (target === "signerBinding" && onSignerBindingLocationChange) {
          onSignerBindingLocationChange(fieldId, loc);
        } else if (target === "subsequentSigning" && onSubsequentSigningLocationChange) {
          onSubsequentSigningLocationChange(fieldId, loc);
        } else if (onFieldLocationChange) {
          onFieldLocationChange(fieldId, loc);
        }
      };

      if (mode === "move") {
        const startClient = percentLocationToClientRect(startPageRectResolved, startLocation);
        const newLeft = startClient.left + dPxX;
        const newTop = startClient.top + dPxY;
        const newRight = startClient.right + dPxX;
        const newBottom = startClient.bottom + dPxY;
        const targetPage =
          (container && findBestPageForClientRect(container, newLeft, newTop, newRight, newBottom)) ||
          startPageEntry || { pageNumber: startPageNumber, rect: startPageRectResolved };
        const pct = clientRectToPercentLocation(targetPage.rect, newLeft, newTop, newRight, newBottom);
        const nextLocation = {
          ...startLocation,
          page: targetPage.pageNumber,
          ...pct,
        };

        emit(nextLocation);

        // Group translate: shift every other selected item by the same pixel delta (cross-page aware).
        const companions = activeDrag.companions;
        if (Array.isArray(companions) && companions.length > 0 && container) {
          if (dPxX !== 0 || dPxY !== 0) {
            for (const comp of companions) {
              applyGroupPixelDeltaToTarget(container, comp, dPxX, dPxY, comp.startLocation);
            }
          }
        }
        return;
      }

      if (mode === "resize" && handle) {
        const startLeft = startLocation.x;
        const startTop = startLocation.y;
        const startRight = startLocation.x + startLocation.width;
        const startBottom = startLocation.y + startLocation.height;
        const startLpx =
          startPageRectResolved.left + (startLeft / 100) * startPageRectResolved.width;
        const startTpx =
          startPageRectResolved.top + (startTop / 100) * startPageRectResolved.height;
        const startRpx =
          startPageRectResolved.left + (startRight / 100) * startPageRectResolved.width;
        const startBpx =
          startPageRectResolved.top + (startBottom / 100) * startPageRectResolved.height;
        const minWpx = (MIN_BOX_SIZE / 100) * startPageRectResolved.width;
        const minHpx = (MIN_BOX_SIZE / 100) * startPageRectResolved.height;
        let l = startLpx;
        let t = startTpx;
        let r = startRpx;
        let b = startBpx;
        if (handle.includes("w")) l = startLpx + dPxX;
        if (handle.includes("e")) r = startRpx + dPxX;
        if (handle.includes("n")) t = startTpx + dPxY;
        if (handle.includes("s")) b = startBpx + dPxY;
        if (r - l < minWpx) {
          if (handle.includes("w")) l = r - minWpx;
          else r = l + minWpx;
        }
        if (b - t < minHpx) {
          if (handle.includes("n")) t = b - minHpx;
          else b = t + minHpx;
        }

        const targetPage =
          (container && findBestPageForClientRect(container, l, t, r, b)) ||
          startPageEntry || { pageNumber: startPageNumber, rect: startPageRectResolved };
        const pct = clientRectToPercentLocation(targetPage.rect, l, t, r, b);
        const nextLocation = {
          ...startLocation,
          page: targetPage.pageNumber,
          ...pct,
        };

        emit(nextLocation);
      }
    };

    const handlePointerUp = () => {
      setActiveDrag(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [
    activeDrag,
    fields,
    onFieldLocationChange,
    onOptionLocationChange,
    onSignatureLocationChange,
    onAdditionalFieldLocationChange,
    onSignerBindingLocationChange,
    onSubsequentSigningLocationChange,
    onLineHeightCaptureChange,
  ]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!toolbarDragRef.current) return;
      const { startX, startY, baseX, baseY } = toolbarDragRef.current;
      setToolbarOffset({
        x: baseX + (event.clientX - startX),
        y: baseY + (event.clientY - startY),
      });
    };
    const handlePointerUp = () => {
      toolbarDragRef.current = null;
      document.body.style.cursor = "";
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, []);

  useEffect(() => {
    if (!identifySectionsMode) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      setCurrentPage(null);
    }
  }, [identifySectionsMode]);

  const getPageRect = (target) => {
    const pageContainer = target.closest("[data-page-container]");
    if (!pageContainer) return null;
    return pageContainer.getBoundingClientRect();
  };

  const startFieldMove = (event, field, markerMeta, opts = {}) => {
    if (
      mode === "annotator" &&
      (markerMeta
        ? hoveredMarkerKey !== markerMeta.key
        : hoveredFieldId !== field.id)
    ) {
      return;
    }
    if (!onFieldLocationChange && !onOptionLocationChange) return;
    const location = markerMeta?.location ?? field.metadata?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    if (event.pointerId != null && event.currentTarget?.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    }
    const primaryIdForSelection = markerMeta?.key ?? field.id;
    const companions = collectCompanions(primaryIdForSelection, { isInGroup: !!opts.isInGroup });
    setActiveDrag({
      fieldId: field.id,
      mode: "move",
      target: markerMeta ? "option" : "field",
      markerMeta,
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
      companions,
    });
  };

  const startFieldResize = (event, field, handle, markerMeta) => {
    if (
      mode === "annotator" &&
      (markerMeta
        ? hoveredMarkerKey !== markerMeta.key
        : hoveredFieldId !== field.id)
    ) {
      return;
    }
    if (!onFieldLocationChange && !onOptionLocationChange) return;
    const location = markerMeta?.location ?? field.metadata?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    if (event.pointerId != null && event.currentTarget?.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    }
    setActiveDrag({
      fieldId: field.id,
      mode: "resize",
      handle,
      target: markerMeta ? "option" : "field",
      markerMeta,
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
    });
  };

  const startAdditionalFieldMove = (event, additionalField, opts = {}) => {
    if (!onAdditionalFieldLocationChange) return;
    const location = additionalField?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      fieldId: additionalField.id,
      mode: "move",
      target: "additional",
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
      companions: collectCompanions(additionalField.id, { isInGroup: !!opts.isInGroup }),
    });
  };

  const startAdditionalFieldResize = (event, additionalField, handle) => {
    if (!onAdditionalFieldLocationChange) return;
    const location = additionalField?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      fieldId: additionalField.id,
      mode: "resize",
      handle,
      target: "additional",
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
    });
  };

  const startSignerBindingMove = (event, binding, opts = {}) => {
    if (!onSignerBindingLocationChange) return;
    const location = binding?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      fieldId: binding.id,
      mode: "move",
      target: "signerBinding",
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
      companions: collectCompanions(binding.id, { isInGroup: !!opts.isInGroup }),
    });
  };

  const startSignerBindingResize = (event, binding, handle) => {
    if (!onSignerBindingLocationChange) return;
    const location = binding?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      fieldId: binding.id,
      mode: "resize",
      handle,
      target: "signerBinding",
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
    });
  };

  const startSubsequentSigningMove = (event, row, opts = {}) => {
    if (!onSubsequentSigningLocationChange) return;
    const location = row?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      fieldId: row.id,
      mode: "move",
      target: "subsequentSigning",
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
      companions: collectCompanions(row.id, { isInGroup: !!opts.isInGroup }),
    });
  };

  const startSubsequentSigningResize = (event, row, handle) => {
    if (!onSubsequentSigningLocationChange) return;
    const location = row?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      fieldId: row.id,
      mode: "resize",
      handle,
      target: "subsequentSigning",
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
    });
  };

  const startSignatureMove = (event, annotation, opts = {}) => {
    if (!onSignatureLocationChange) return;
    if (mode === "annotator" && hoveredSignatureId !== annotation.id) {
      return;
    }
    const location = annotation?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      fieldId: annotation.id,
      mode: "move",
      target: "signature",
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
      companions: collectCompanions(annotation.id, { isInGroup: !!opts.isInGroup }),
    });
  };

  const startSignatureResize = (event, annotation, handle) => {
    if (!onSignatureLocationChange) return;
    if (mode === "annotator" && hoveredSignatureId !== annotation.id) {
      return;
    }
    const location = annotation?.pdfLocation;
    if (!location) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    // Match startFieldResize: capture the pointer so the drag keeps tracking even
    // if the cursor leaves the small handle element.
    if (event.pointerId != null && event.currentTarget?.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    }
    setActiveDrag({
      fieldId: annotation.id,
      mode: "resize",
      handle,
      target: "signature",
      startX: event.clientX,
      startY: event.clientY,
      startLocation: location,
      pageRect,
    });
  };

  const handleSignatureDelete = (annotation) => {
    if (!onDeleteSignature) return;
    const confirmed = window.confirm("Delete this signature annotation?");
    if (confirmed) {
      onDeleteSignature(annotation.id);
    }
  };

  const handleAdditionalFieldDelete = (additionalFieldOrId) => {
    if (!onDeleteAdditionalField) return;
    const id = typeof additionalFieldOrId === "string" ? additionalFieldOrId : additionalFieldOrId?.id;
    if (!id) return;
    const confirmed = window.confirm("Delete this additional annotation?");
    if (confirmed) {
      onDeleteAdditionalField(id);
    }
  };

  const handleSignerBindingDelete = (bindingOrId) => {
    if (!onDeleteSignerBinding) return;
    const id = typeof bindingOrId === "string" ? bindingOrId : bindingOrId?.id;
    if (!id) return;
    const confirmed = window.confirm("Delete this persona (signer) attribute box?");
    if (confirmed) {
      onDeleteSignerBinding(id);
    }
  };

  const handleSubsequentSigningDelete = (rowOrId) => {
    if (!onDeleteSubsequentSigningBinding) return;
    const id = typeof rowOrId === "string" ? rowOrId : rowOrId?.id;
    if (!id) return;
    const confirmed = window.confirm("Delete this subsequent signing PDF binding?");
    if (confirmed) {
      onDeleteSubsequentSigningBinding(id);
    }
  };

  const selectedSignatureAnnotation = selectedSignatureId
    ? mergedSignatureAnnotations.find((a) => a.id === selectedSignatureId)
    : null;
  const selectedSignatureLoc = selectedSignatureAnnotation?.pdfLocation;
  const signaturePage = selectedSignatureLoc?.page;

  // Keep a ref to the latest location so consecutive nudges (clicks or key repeat) use the updated base before parent re-renders.
  useEffect(() => {
    signatureLocRef.current = selectedSignatureLoc ?? null;
  }, [selectedSignatureLoc]);

  // Nudge/resize signature by pixel deltas (same as field annotations: 1px at a time)
  const applySignatureNudge = useCallback(
    (dxPx, dyPx, dwPx, dhPx) => {
      if (!selectedSignatureId || !onSignatureLocationChange || signaturePage == null) return;
      const loc = signatureLocRef.current ?? selectedSignatureLoc;
      if (!loc) return;
      const pageEl = pdfContainerRef.current?.querySelector(`[data-page-number="${signaturePage}"]`);
      const rect = pageEl?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return;
      const dx = (dxPx / rect.width) * 100;
      const dy = (dyPx / rect.height) * 100;
      const dw = (dwPx / rect.width) * 100;
      const dh = (dhPx / rect.height) * 100;
      const w = clamp((loc.width ?? 10) + dw, MIN_BOX_SIZE, 100);
      const h = clamp((loc.height ?? 10) + dh, MIN_BOX_SIZE, 100);
      const next = {
        ...loc,
        x: clamp((loc.x ?? 0) + dx, 0, 100 - w),
        y: clamp((loc.y ?? 0) + dy, 0, 100 - h),
        width: w,
        height: h,
      };
      signatureLocRef.current = next;
      onSignatureLocationChange(selectedSignatureId, next);
    },
    [selectedSignatureId, onSignatureLocationChange, selectedSignatureLoc, signaturePage],
  );

  useEffect(() => {
    if (!selectedSignatureId || !onSignatureLocationChange) return;
    const handleKeyDown = (e) => {
      if (e.target.closest("input") || e.target.closest("textarea") || e.target.closest("[contenteditable]")) return;
      const ann = mergedSignatureAnnotations.find((a) => a.id === selectedSignatureId);
      if (!ann?.pdfLocation) return;
      const isAlt = e.altKey;
      let dxPx = 0, dyPx = 0, dwPx = 0, dhPx = 0;
      switch (e.key) {
        case "ArrowLeft":  if (isAlt) dwPx = -1; else dxPx = -1; break;
        case "ArrowRight": if (isAlt) dwPx = 1;  else dxPx = 1;  break;
        case "ArrowUp":    if (isAlt) dhPx = -1; else dyPx = -1; break;
        case "ArrowDown":  if (isAlt) dhPx = 1;  else dyPx = 1;  break;
        default: return;
      }
      e.preventDefault();
      applySignatureNudge(dxPx, dyPx, dwPx, dhPx);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedSignatureId, mergedSignatureAnnotations, onSignatureLocationChange, applySignatureNudge]);

  // Single nudge/resize handler for bottom toolbar: applies to signature, field, or additional depending on selection
  const handleNudgeResize = (dxPx, dyPx, dwPx, dhPx) => {
    if (selectedSignatureId && onSignatureLocationChange && selectedSignatureLoc) applySignatureNudge(dxPx, dyPx, dwPx, dhPx);
    else if (
      selectedNudgeTarget &&
      (onFieldLocationChange ||
        onOptionLocationChange ||
        (selectedNudgeTarget.type === "additional" && onAdditionalFieldLocationChange) ||
        (selectedNudgeTarget.type === "subsequentSigning" && onSubsequentSigningLocationChange) ||
        (selectedNudgeTarget.type === "signerBinding" && onSignerBindingLocationChange) ||
        (selectedNudgeTarget.type === "signatureAnnotation" && onSignatureLocationChange))
    )
      applyNudge(dxPx, dyPx, dwPx, dhPx);
  };

  const startLineHeightCaptureMove = (event, box) => {
    if (!onLineHeightCaptureChange) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      mode: "move",
      target: "lineHeightCapture",
      lineHeightPage: box.page,
      startX: event.clientX,
      startY: event.clientY,
      startLocation: { x: box.x, y: box.y, width: box.width, height: box.height },
      pageRect,
    });
  };

  const startLineHeightCaptureResize = (event, box, handle) => {
    if (!onLineHeightCaptureChange) return;
    const pageRect = getPageRect(event.currentTarget);
    if (!pageRect) return;
    setActiveDrag({
      mode: "resize",
      handle,
      target: "lineHeightCapture",
      lineHeightPage: box.page,
      startX: event.clientX,
      startY: event.clientY,
      startLocation: { x: box.x, y: box.y, width: box.width, height: box.height },
      pageRect,
    });
  };

  // Calculate selection box style (supports rubber-band across pages)
  const getSelectionBoxStyle = (pageNumber) => {
    if (!isDragging || !dragStart || !dragEnd || currentPage == null) {
      return null;
    }
    const container = pdfContainerRef.current;
    if (!container) return null;
    const startPage = currentPage;
    const endPage = dragEnd.page ?? startPage;
    const sPg = findPageRectForPageNumber(container, startPage);
    const ePg = findPageRectForPageNumber(container, endPage);
    if (!sPg || !ePg) return null;
    const sx1 = sPg.rect.left + (dragStart.x / 100) * sPg.rect.width;
    const sy1 = sPg.rect.top + (dragStart.y / 100) * sPg.rect.height;
    const ex1 = ePg.rect.left + (dragEnd.x / 100) * ePg.rect.width;
    const ey1 = ePg.rect.top + (dragEnd.y / 100) * ePg.rect.height;
    const left = Math.min(sx1, ex1);
    const right = Math.max(sx1, ex1);
    const top = Math.min(sy1, ey1);
    const bottom = Math.max(sy1, ey1);
    const pR = findPageRectForPageNumber(container, pageNumber)?.rect;
    if (!pR) return null;
    const il = Math.max(left, pR.left);
    const ir = Math.min(right, pR.right);
    const it = Math.max(top, pR.top);
    const ib = Math.min(bottom, pR.bottom);
    if (ir <= il || ib <= it) return null;
    const leftPct = ((il - pR.left) / pR.width) * 100;
    const topPct = ((it - pR.top) / pR.height) * 100;
    const widthPct = ((ir - il) / pR.width) * 100;
    const heightPct = ((ib - it) / pR.height) * 100;

    return {
      position: "absolute",
      left: `${leftPct}%`,
      top: `${topPct}%`,
      width: `${widthPct}%`,
      height: `${heightPct}%`,
      border: "2px dashed #00a896",
      backgroundColor: "rgba(0, 168, 150, 0.1)",
      pointerEvents: "none",
      zIndex: 1400,
    };
  };

  return (
    <Box
      id="pdf-viewer-container"
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.default",
        }}
      >
        <Typography variant="subtitle2" sx={{ ml: 1, color: "text.primary" }}>
          {pdfFile.name} (Drag to select field areas)
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {(mode === "annotator" || mode === "json") && onLineHeightSet && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {lineHeightSettingMode ? (
                <>
                  <Button size="small" variant="contained" color="primary" onClick={confirmLineHeightFromBox}>
                    Confirm
                  </Button>
                  <Button size="small" onClick={onLineHeightCancel}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {defaultLineHeightPx != null && (
                    <Typography component="span" variant="caption" sx={{ fontFamily: "monospace", opacity: 0.9 }}>
                      {defaultLineHeightPx} px
                    </Typography>
                  )}
                  <Button size="small" variant="outlined" onClick={onLineHeightSet}>
                    Set line height
                  </Button>
                </>
              )}
            </Box>
          )}
          {studioFormFlag?.visible && studioFormFlag?.onFlagIconClick && (
            <Tooltip
              title={
                studioFormFlag.isFlagged && studioFormFlag.note ? (
                  <Box component="span" sx={{ display: "block", maxWidth: 280, whiteSpace: "pre-wrap" }}>
                    {studioFormFlag.note}
                  </Box>
                ) : (
                  "Flag for follow-up"
                )
              }
              slotProps={{
                popper: { sx: { "& .MuiTooltip-tooltip": { maxWidth: 320 } } },
              }}
            >
              <IconButton
                size="small"
                aria-label={studioFormFlag.isFlagged ? "Unflag form" : "Flag form"}
                onClick={studioFormFlag.onFlagIconClick}
                sx={{
                  color: studioFormFlag.isFlagged ? "primary.main" : "action.active",
                }}
              >
                {studioFormFlag.isFlagged ? (
                  <FlagIcon sx={{ fontSize: 20 }} />
                ) : (
                  <OutlinedFlagIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {mode === "json" && jsonMarkComplete?.visible && (
            <Button
              variant={jsonMarkComplete.isComplete ? "outlined" : "contained"}
              color={jsonMarkComplete.isComplete ? "secondary" : "primary"}
              size="small"
              onClick={jsonMarkComplete.onToggle}
            >
              {jsonMarkComplete.isComplete ? "Mark JSON incomplete" : "Mark JSON complete"}
            </Button>
          )}
          {mode === "annotator" && annotatorMarkComplete?.visible && (
            <Button
              variant={annotatorMarkComplete.isComplete ? "outlined" : "contained"}
              color={annotatorMarkComplete.isComplete ? "secondary" : "primary"}
              size="small"
              onClick={annotatorMarkComplete.onToggle}
            >
              {annotatorMarkComplete.isComplete ? "Mark incomplete" : "Mark annotation complete"}
            </Button>
          )}
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              savePdfWithAnnotations(pdfFile, fields, mergedSignatureAnnotations);
            }}
          >
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            Save with Annotations
          </MenuItem>
        </Menu>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        {/* Thumbnail Sidebar */}
        {showThumbnails && (
          <Box
            sx={{
              width: "150px",
              bgcolor: isDarkMode ? "grey.900" : "grey.300",
              overflowY: "auto",
              borderRight: 1,
              borderColor: "divider",
              p: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ px: 1, fontWeight: "bold", color: "text.secondary" }}
            >
              Pages
            </Typography>
            <Document file={pdfFile}>
              {Array.from(new Array(numPages), (el, index) => (
                <Box
                  key={`thumb_${index + 1}`}
                  onClick={() => scrollToPage(index + 1)}
                  sx={{
                    mt: 1,
                    cursor: "pointer",
                    border: 2,
                    borderColor:
                      selectedPage === index + 1
                        ? "primary.main"
                        : "transparent",
                    borderRadius: 1,
                    overflow: "hidden",
                    transition: "all 0.2s",
                    position: "relative",
                    "&:hover": {
                      borderColor: "primary.light",
                      boxShadow: 2,
                    },
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={130}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                  {isDarkMode && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: "rgba(0, 0, 0, 0.2)",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{
                      display: "block",
                      bgcolor:
                        selectedPage === index + 1
                          ? "primary.main"
                          : isDarkMode
                          ? "grey.800"
                          : "grey.400",
                      color:
                        selectedPage === index + 1 ? "white" : "text.primary",
                      py: 0.5,
                    }}
                  >
                    {index + 1}
                  </Typography>
                </Box>
              ))}
            </Document>
          </Box>
        )}

        {/* Main PDF Viewer: outer scrolls; inner max-content + minWidth 100% so horizontal scroll includes full centered page width. */}
        <Box
          ref={pdfContainerRef}
          sx={{
            flexGrow: 1,
            bgcolor: isDarkMode ? "grey.900" : "grey.200",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              p: 2,
              boxSizing: "border-box",
              width: "max-content",
              minWidth: "100%",
            }}
          >
          <Document
            file={pdfFile}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                Loading PDF...
              </Typography>
            }
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Box
                key={`page_${index + 1}`}
                data-page-container
                data-page-number={index + 1}
                sx={{
                  position: "relative",
                  cursor: textHighlightMode ? "text" : "crosshair",
                  userSelect: textHighlightMode ? "text" : "none",
                }}
                onMouseDown={(e) => handleMouseDown(e, index + 1)}
                onMouseMove={(e) => handleMouseMove(e, index + 1)}
                onMouseUp={(e) => handleMouseUp(e, index + 1)}
                onMouseLeave={handleMouseLeave}
              >
                <Page
                  pageNumber={index + 1}
                  width={900 * scale}
                  renderTextLayer={textHighlightMode}
                  renderAnnotationLayer={false}
                />

                {/* Overlays: when textHighlightMode, pointer-events none so text layer can receive selection */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: textHighlightMode ? "none" : "auto",
                    zIndex: 10,
                  }}
                >
                {/* Sunglasses Tint for Dark Mode */}
                {isDarkMode && tintEnabled && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: "rgba(0, 0, 0, 0.30)",
                      pointerEvents: "none",
                      zIndex: 5,
                      mixBlendMode: "multiply",
                    }}
                  />
                )}

                {/* Selection Box Overlay */}
                {getSelectionBoxStyle(index + 1) && (
                  <Box sx={getSelectionBoxStyle(index + 1)} />
                )}

                {/* Line height capture box (annotator only) */}
                {lineHeightSettingMode &&
                  lineHeightCaptureBox &&
                  lineHeightCaptureBox.page === index + 1 && (
                    <LineHeightCaptureBox
                      box={lineHeightCaptureBox}
                      onStartMove={startLineHeightCaptureMove}
                      onStartResize={startLineHeightCaptureResize}
                    />
                  )}

                {/* Render Field Markers */}
                {mergedSignatureAnnotations
                  .filter(
                    (annotation) => annotation?.pdfLocation?.page === index + 1
                  )
                  .map((annotation) => (
                    <SignatureMarker
                      key={annotation.id}
                      annotation={annotation}
                      isSelected={
                        isControlledSelection
                          ? selectedFieldIds.includes(annotation.id) &&
                            (primarySelectId == null || primarySelectId === annotation.id)
                          : selectedSignatureId === annotation.id
                      }
                      isGroupSelected={
                        isControlledSelection &&
                        selectedFieldIds.length > 1 &&
                        selectedFieldIds.includes(annotation.id)
                      }
                      onSelect={(signatureId, opts) => {
                        if (onFieldSelect) onFieldSelect(signatureId, opts);
                        setSelectedSignatureId(signatureId);
                        if (onSignatureSelect) {
                          onSignatureSelect(signatureId);
                        }
                      }}
                      onStartMove={startSignatureMove}
                      onStartResize={startSignatureResize}
                      onHoverChange={setHoveredSignatureId}
                      onRequestDelete={handleSignatureDelete}
                      hideLabel={hideLabelAndIndex}
                    />
                  ))}
                {fields
                  .filter(
                    (field) =>
                      shouldRenderSectionFieldMainMarker(field) &&
                      field.metadata?.pdfLocation?.page === index + 1,
                  )
                  .map((field) => (
                    <FieldMarker
                      key={field.id}
                      field={field}
                      isSelected={
                        isControlledSelection
                          ? selectedFieldIds.includes(field.id) && (primarySelectId == null || primarySelectId === field.id)
                          : selectedFieldId === field.id
                      }
                      isGroupSelected={
                        isControlledSelection
                          ? selectedFieldIds.length > 1 && selectedFieldIds.includes(field.id)
                          : identifySectionsMode && selectedFieldIds.includes(field.id)
                      }
                      onSelect={handleSelectField}
                      onEditField={onEditField}
                      onAssignFieldToSection={onAssignFieldToSection}
                      identifySectionsMode={identifySectionsMode}
                      onStartMove={startFieldMove}
                      onStartResize={startFieldResize}
                      onHoverChange={(fieldId, markerKey) => {
                        setHoveredFieldId(fieldId);
                        setHoveredMarkerKey(markerKey);
                      }}
                      isAnnotatorMode={mode === "annotator"}
                      selectId={field.id}
                      legendIndex={legendIndexByKey.get(field.id)}
                      hideResizeHandles={hideResizeHandles}
                      hideLabelAndIndex={hideLabelAndIndex}
                      glow={field.id === glowFieldId}
                    />
                  ))}
                {fields.map((field) =>
                  buildOptionMarkers(field)
                    .filter((marker) => marker.location?.page === index + 1)
                    .map((marker) => (
                      <FieldMarker
                        key={marker.key}
                        field={{
                          ...field,
                          metadata: {
                            ...field.metadata,
                            pdfLocation: marker.location,
                          },
                        }}
                        isSelected={
                          isControlledSelection
                            ? selectedFieldIds.includes(field.id) && (primarySelectId != null ? primarySelectId === marker.key : selectedFieldIds.length === 1)
                            : selectedFieldId === marker.key
                        }
                        isGroupSelected={
                          isControlledSelection
                            ? selectedFieldIds.length > 1 && selectedFieldIds.includes(field.id)
                            : identifySectionsMode && selectedFieldIds.includes(field.id)
                        }
                        onSelect={handleSelectField}
                        onEditField={onEditField}
                        onAssignFieldToSection={onAssignFieldToSection}
                        identifySectionsMode={identifySectionsMode}
                        onStartMove={startFieldMove}
                        onStartResize={startFieldResize}
                        onHoverChange={(fieldId, markerKey) => {
                          setHoveredFieldId(fieldId);
                          setHoveredMarkerKey(markerKey);
                        }}
                        isAnnotatorMode={mode === "annotator"}
                        isOptionMarker
                        optionLabel={marker.label}
                        selectId={marker.key}
                        markerMeta={marker}
                        legendIndex={legendIndexByKey.get(marker.key)}
                        hideResizeHandles={hideResizeHandles}
                        hideLabelAndIndex={hideLabelAndIndex}
                        glow={false}
                      />
                    ))
                )}
                {(additionalFields || [])
                  .filter((af) => af.pdfLocation?.page === index + 1)
                  .map((af) => (
                    <FieldMarker
                      key={af.id}
                      field={{
                        id: af.id,
                        label: af.attributeName,
                        attributeName: af.attributeName,
                        metadata: { pdfLocation: af.pdfLocation },
                      }}
                      isSelected={primarySelectedId === af.id}
                      isGroupSelected={
                        isControlledSelection &&
                        selectedFieldIds.length > 1 &&
                        selectedFieldIds.includes(af.id)
                      }
                      onSelect={handleSelectField}
                      onEditField={() => {}}
                      onAssignFieldToSection={undefined}
                      identifySectionsMode={false}
                      onStartMove={(e, field, _marker, opts) => {
                        const a = (additionalFields || []).find((x) => x.id === field.id);
                        if (a) startAdditionalFieldMove(e, a, opts);
                      }}
                      onStartResize={(e, field, handle) => {
                        const a = (additionalFields || []).find((x) => x.id === field.id);
                        if (a) startAdditionalFieldResize(e, a, handle);
                      }}
                      onHoverChange={(fieldId) => {
                        setHoveredFieldId(fieldId);
                        setHoveredMarkerKey(null);
                      }}
                      isAnnotatorMode={mode === "annotator"}
                      selectId={af.id}
                      legendIndex={legendIndexByKey.get(af.id)}
                      hideResizeHandles={hideResizeHandles}
                      hideLabelAndIndex={hideLabelAndIndex}
                      glow={false}
                      onRequestDelete={onDeleteAdditionalField ? () => handleAdditionalFieldDelete(af.id) : undefined}
                    />
                  ))}
                {(mergedSignerAttributeBindings || [])
                  .filter((sb) => sb.pdfLocation?.page === index + 1)
                  .map((sb) => (
                    <FieldMarker
                      key={sb.id}
                      field={{
                        id: sb.id,
                        label: `${sb.attributeName || "Persona"} · ${sb.signerKey}`,
                        attributeName: sb.attributeName,
                        metadata: { pdfLocation: sb.pdfLocation },
                      }}
                      isSelected={primarySelectedId === sb.id}
                      isGroupSelected={
                        isControlledSelection &&
                        selectedFieldIds.length > 1 &&
                        selectedFieldIds.includes(sb.id)
                      }
                      onSelect={handleSelectField}
                      onEditField={() => {}}
                      onAssignFieldToSection={undefined}
                      identifySectionsMode={false}
                      onStartMove={(e, _field, _marker, opts) => {
                        const b = (mergedSignerAttributeBindings || []).find((x) => x.id === sb.id);
                        if (b) startSignerBindingMove(e, b, opts);
                      }}
                      onStartResize={(e, field, handle) => {
                        const b = (mergedSignerAttributeBindings || []).find((x) => x.id === field.id);
                        if (b) startSignerBindingResize(e, b, handle);
                      }}
                      onHoverChange={(fieldId) => {
                        setHoveredFieldId(fieldId);
                        setHoveredMarkerKey(null);
                      }}
                      isAnnotatorMode={mode === "annotator"}
                      selectId={sb.id}
                      legendIndex={legendIndexByKey.get(sb.id)}
                      hideResizeHandles={hideResizeHandles}
                      hideLabelAndIndex={hideLabelAndIndex}
                      glow={false}
                      onRequestDelete={onDeleteSignerBinding ? () => handleSignerBindingDelete(sb.id) : undefined}
                    />
                  ))}
                {(subsequentSigningBindings || [])
                  .filter((ss) => ss.pdfLocation?.page === index + 1)
                  .map((ss) => (
                    <FieldMarker
                      key={ss.id}
                      field={{
                        id: ss.id,
                        label: ss.attributeName || "Subsequent signing",
                        attributeName: ss.attributeName,
                        metadata: { pdfLocation: ss.pdfLocation },
                      }}
                      isSelected={primarySelectedId === ss.id}
                      isGroupSelected={
                        isControlledSelection &&
                        selectedFieldIds.length > 1 &&
                        selectedFieldIds.includes(ss.id)
                      }
                      onSelect={handleSelectField}
                      onEditField={() => {}}
                      onAssignFieldToSection={undefined}
                      identifySectionsMode={false}
                      onStartMove={(e, _field, _marker, opts) => {
                        const row = (subsequentSigningBindings || []).find((x) => x.id === ss.id);
                        if (row) startSubsequentSigningMove(e, row, opts);
                      }}
                      onStartResize={(e, field, handle) => {
                        const row = (subsequentSigningBindings || []).find((x) => x.id === field.id);
                        if (row) startSubsequentSigningResize(e, row, handle);
                      }}
                      onHoverChange={(fieldId) => {
                        setHoveredFieldId(fieldId);
                        setHoveredMarkerKey(null);
                      }}
                      isAnnotatorMode={mode === "annotator"}
                      selectId={ss.id}
                      legendIndex={legendIndexByKey.get(ss.id)}
                      hideResizeHandles={hideResizeHandles}
                      hideLabelAndIndex={hideLabelAndIndex}
                      glow={false}
                      onRequestDelete={
                        onDeleteSubsequentSigningBinding
                          ? () => handleSubsequentSigningDelete(ss.id)
                          : undefined
                      }
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Document>
          {legendItems.length > 0 && (
            <Paper
              elevation={2}
              sx={{
                width: "100%",
                maxWidth: 900 * scale,
                mt: 3,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Index / Legend
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {legendItems.map((item, index) => (
                  <Box
                    key={item.key}
                    onClick={() => scrollToAnnotation(item)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "action.hover",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "action.selected",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 22,
                        height: 22,
                        borderRadius: "999px",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      p{item.page}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
          </Box>
        </Box>
      </Box>

      {/* Floating type-picker / quick-create: shown after drawing a box in annotator mode */}
      {pendingBox && (() => {
        const clearPending = () => {
          setPendingBox(null);
          setPendingBoxType(null);
          setCheckboxOptions([]);
          setPendingAdditionalAttributeName("");
          setPendingAdditionalLines(0);
          setPendingDuplicateAttributeName("");
          setPendingPersonaAttributeName("");
          setPendingPersonaLines(0);
          setPendingPersonaRoleSignerKey("");
          setDragStart(null);
          setDragEnd(null);
          setCurrentPage(null);
        };

        // Clamp popup position so it stays on screen
        const popupLeft = Math.min(pendingBox.screenX + 12, window.innerWidth - 200);
        const popupTop = Math.min(pendingBox.screenY + 12, window.innerHeight - 320);

        return (
          <Box
            sx={{
              position: "fixed",
              top: popupTop,
              left: popupLeft,
              zIndex: 9999,
            }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 1,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                border: "2px solid",
                borderColor: "primary.main",
                bgcolor: "background.paper",
                minWidth: 160,
                maxWidth: 240,
              }}
            >
              {/* ── Step 1: type chooser ── */}
              {!pendingBoxType && (
                <>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}>
                    FIELD TYPE
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    fullWidth
                    sx={{ justifyContent: "flex-start", textTransform: "none", fontSize: "0.82rem", py: 0.6 }}
                    onClick={() => {
                      // Input: instant create — no dialog
                      if (onInstantCreate) {
                        const box = pendingBox;
                        clearPending();
                        onInstantCreate("input", { page: box.page, x: box.x, y: box.y, width: box.width, height: box.height });
                      } else {
                        const box = pendingBox;
                        clearPending();
                        if (onPdfClick) onPdfClick(box.page, box.x, box.y, box.width, box.height, "input");
                      }
                    }}
                  >
                    Input
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    sx={{ justifyContent: "flex-start", textTransform: "none", fontSize: "0.82rem", py: 0.6 }}
                    onClick={() => {
                      setPendingBoxType("checkbox");
                      setCheckboxOptions(["checkbox1"]);
                    }}
                  >
                    Checkbox
                  </Button>
                  {/* Annotators place Input/Checkbox bboxes only; full types in JSON / other modes */}
                  {mode !== "annotator" && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={{ justifyContent: "flex-start", textTransform: "none", fontSize: "0.82rem", py: 0.6 }}
                        onClick={() => {
                          setPendingBoxType("decision");
                          setCheckboxOptions(["Option 1", "Option 2"]);
                        }}
                      >
                        Decision
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        sx={{ justifyContent: "flex-start", textTransform: "none", fontSize: "0.82rem", py: 0.6 }}
                        onClick={() => {
                          setPendingBoxType("decisionWithOther");
                          setCheckboxOptions(["Option 1", "Option 2"]);
                        }}
                      >
                        Decision with Other
                      </Button>
                    </>
                  )}
                  {mode !== "annotator" &&
                    onAddSignerBindingAnnotation &&
                    signerOptionsFlat.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={{ justifyContent: "flex-start", textTransform: "none", fontSize: "0.82rem", py: 0.6, borderColor: "info.main", color: "info.main" }}
                      onClick={() => {
                        setPendingBoxType("persona");
                        setPendingPersonaAttributeName("");
                        setPendingPersonaLines(0);
                        setPendingPersonaRoleSignerKey(signerOptionsFlat[0]?.compound ?? "");
                      }}
                    >
                      Persona attribute (signer)
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    fullWidth
                    sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.3, color: "text.secondary" }}
                    onClick={clearPending}
                  >
                    Cancel
                  </Button>
                </>
              )}

              {/* ── Step 2: checkbox / decision option builder ── */}
              {["checkbox", "decision", "decisionWithOther"].includes(pendingBoxType) && (
                <>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}>
                    {pendingBoxType === "checkbox"
                      ? "CHECKBOX OPTIONS"
                      : pendingBoxType === "decisionWithOther"
                        ? "DECISION OPTIONS (+ OTHER INPUT)"
                        : "DECISION OPTIONS"}
                  </Typography>
                  {checkboxOptions.map((opt, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      <input
                        autoFocus={i === checkboxOptions.length - 1}
                        value={opt}
                        placeholder={`Option ${i + 1}`}
                        onChange={(e) => {
                          const next = [...checkboxOptions];
                          next[i] = e.target.value;
                          setCheckboxOptions(next);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            setCheckboxOptions((prev) => [...prev, `checkbox${prev.length + 1}`]);
                          }
                          if (e.key === "Escape") clearPending();
                        }}
                        style={{
                          flex: 1,
                          fontSize: "0.82rem",
                          padding: "3px 6px",
                          borderRadius: 4,
                          border: "1px solid #aaa",
                          outline: "none",
                          background: "transparent",
                          color: "inherit",
                          minWidth: 0,
                        }}
                      />
                      {checkboxOptions.length > 1 && (
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={() => setCheckboxOptions((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          ×
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Button
                    size="small"
                    variant="text"
                    sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.2, justifyContent: "flex-start" }}
                    onClick={() => setCheckboxOptions((prev) => [...prev, `checkbox${prev.length + 1}`])}
                  >
                    + Add option
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={checkboxOptions.every((o) => !o.trim())}
                    sx={{ textTransform: "none", fontSize: "0.82rem", py: 0.5, mt: 0.25 }}
                    onClick={() => {
                      const opts = checkboxOptions.filter((o) => o.trim());
                      const box = pendingBox;
                      const instantType =
                        pendingBoxType === "checkbox"
                          ? "checkbox"
                          : pendingBoxType === "decisionWithOther"
                            ? "decisionWithOther"
                            : "decision";
                      clearPending();
                      if (onInstantCreate) {
                        onInstantCreate(
                          instantType,
                          { page: box.page, x: box.x, y: box.y, width: box.width, height: box.height },
                          opts,
                        );
                      } else if (onPdfClick) {
                        onPdfClick(box.page, box.x, box.y, box.width, box.height, instantType);
                      }
                    }}
                  >
                    {pendingBoxType === "checkbox"
                      ? "Create Checkbox"
                      : pendingBoxType === "decisionWithOther"
                        ? "Create Decision with Other"
                        : "Create Decision"}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    fullWidth
                    sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.2, color: "text.secondary" }}
                    onClick={() => { setPendingBoxType(null); setCheckboxOptions([]); }}
                  >
                    ← Back
                  </Button>
                </>
              )}

              {/* ── Step 2: persona / signer attribute (published under signaturesJSON signer fields[]) ── */}
              {pendingBoxType === "persona" && (
                <>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}>
                    PERSONA ATTRIBUTE (SIGNER)
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <select
                      value={pendingPersonaRoleSignerKey}
                      onChange={(e) => setPendingPersonaRoleSignerKey(e.target.value)}
                      style={{
                        fontSize: "0.82rem",
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: "1px solid",
                        borderColor: "var(--mui-palette-divider)",
                        outline: "none",
                        background: "var(--mui-palette-background-paper)",
                        color: "inherit",
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      {signerOptionsFlat.map((o) => (
                        <option key={o.compound} value={o.compound}>{o.label}</option>
                      ))}
                    </select>
                    <input
                      autoFocus
                      value={pendingPersonaAttributeName}
                      placeholder="Attribute name (e.g. buyer1.legal_name)"
                      onChange={(e) => setPendingPersonaAttributeName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") clearPending(); }}
                      style={{
                        fontSize: "0.82rem",
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: "1px solid",
                        borderColor: "divider",
                        outline: "none",
                        background: "transparent",
                        color: "inherit",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>Lines:</Typography>
                      <input
                        type="number"
                        min={0}
                        value={pendingPersonaLines || ""}
                        placeholder="0"
                        onChange={(e) => setPendingPersonaLines(e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0))}
                        style={{
                          width: 56,
                          fontSize: "0.82rem",
                          padding: "2px 6px",
                          borderRadius: 4,
                          border: "1px solid",
                          borderColor: "divider",
                          outline: "none",
                          background: "transparent",
                          color: "inherit",
                        }}
                      />
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={!pendingPersonaAttributeName.trim() || !pendingPersonaRoleSignerKey}
                    sx={{ textTransform: "none", fontSize: "0.82rem", py: 0.5, mt: 0.25 }}
                    onClick={() => {
                      const attributeName = pendingPersonaAttributeName.trim();
                      const opt = signerOptionsFlat.find((o) => o.compound === pendingPersonaRoleSignerKey);
                      if (!attributeName || !opt || !onAddSignerBindingAnnotation) return;
                      const box = pendingBox;
                      const pdfLocation = { page: box.page, x: box.x, y: box.y, width: box.width, height: box.height };
                      if (pendingPersonaLines > 0) pdfLocation.lines = pendingPersonaLines;
                      clearPending();
                      onAddSignerBindingAnnotation({
                        roleKey: opt.roleKey,
                        signerKey: opt.signerKey,
                        attributeName,
                        pdfLocation,
                        lines: pendingPersonaLines || 0,
                      });
                    }}
                  >
                    Add persona box
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    fullWidth
                    sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.2, color: "text.secondary" }}
                    onClick={() => {
                      setPendingBoxType(null);
                      setPendingPersonaAttributeName("");
                      setPendingPersonaLines(0);
                      setPendingPersonaRoleSignerKey("");
                    }}
                  >
                    ← Back
                  </Button>
                </>
              )}

              {/* ── Step 2: additional annotation (attributeName + lines) — form / deal data → additionalFieldsJSON ── */}
              {pendingBoxType === "additional" && (
                <>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}>
                    ADDITIONAL (FORM DATA)
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <input
                      autoFocus
                      value={pendingAdditionalAttributeName}
                      placeholder="Attribute name (e.g. brokerName)"
                      onChange={(e) => setPendingAdditionalAttributeName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") clearPending(); }}
                      style={{
                        fontSize: "0.82rem",
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: "1px solid",
                        borderColor: "divider",
                        outline: "none",
                        background: "transparent",
                        color: "inherit",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>Lines:</Typography>
                      <input
                        type="number"
                        min={0}
                        value={pendingAdditionalLines || ""}
                        placeholder="0"
                        onChange={(e) => setPendingAdditionalLines(e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0))}
                        style={{
                          width: 56,
                          fontSize: "0.82rem",
                          padding: "2px 6px",
                          borderRadius: 4,
                          border: "1px solid",
                          borderColor: "divider",
                          outline: "none",
                          background: "transparent",
                          color: "inherit",
                        }}
                      />
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={!pendingAdditionalAttributeName.trim()}
                    sx={{ textTransform: "none", fontSize: "0.82rem", py: 0.5, mt: 0.25 }}
                    onClick={() => {
                      const attributeName = pendingAdditionalAttributeName.trim();
                      if (!attributeName || !onAddAdditionalAnnotation) return;
                      const box = pendingBox;
                      const pdfLocation = { page: box.page, x: box.x, y: box.y, width: box.width, height: box.height };
                      if (pendingAdditionalLines > 0) pdfLocation.lines = pendingAdditionalLines;
                      clearPending();
                      onAddAdditionalAnnotation({ attributeName, pdfLocation, lines: pendingAdditionalLines || 0 });
                    }}
                  >
                    Add annotation
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    fullWidth
                    sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.2, color: "text.secondary" }}
                    onClick={() => { setPendingBoxType(null); setPendingAdditionalAttributeName(""); setPendingAdditionalLines(0); }}
                  >
                    ← Back
                  </Button>
                </>
              )}

              {/* ── Step 2: duplicate of existing field ── */}
              {pendingBoxType === "duplicate" && (
                <>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}>
                    PLACE DUPLICATE OF FIELD
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <select
                      value={pendingDuplicateAttributeName}
                      onChange={(e) => setPendingDuplicateAttributeName(e.target.value)}
                      style={{
                        fontSize: "0.82rem",
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: "1px solid",
                        borderColor: "var(--mui-palette-divider)",
                        outline: "none",
                        background: "var(--mui-palette-background-paper)",
                        color: "inherit",
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      {(() => {
                        const seen = new Set();
                        return (fields || []).filter((f) => f.attributeName && !seen.has(f.attributeName) && seen.add(f.attributeName)).map((f) => (
                          <option key={f.id} value={f.attributeName}>{f.label ?? f.attributeName}</option>
                        ));
                      })()}
                    </select>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={!pendingDuplicateAttributeName}
                    sx={{ textTransform: "none", fontSize: "0.82rem", py: 0.5, mt: 0.25 }}
                    onClick={() => {
                      if (!pendingDuplicateAttributeName || !onAddAdditionalAnnotation) return;
                      const box = pendingBox;
                      const pdfLocation = { page: box.page, x: box.x, y: box.y, width: box.width, height: box.height };
                      clearPending();
                      onAddAdditionalAnnotation({ attributeName: pendingDuplicateAttributeName, pdfLocation, lines: 0 });
                    }}
                  >
                    Place duplicate
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    fullWidth
                    sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.2, color: "text.secondary" }}
                    onClick={() => { setPendingBoxType(null); setPendingDuplicateAttributeName(""); }}
                  >
                    ← Back
                  </Button>
                </>
              )}
            </Paper>
          </Box>
        );
      })()}

      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: 16,
          transform: `translate(calc(-50% + ${toolbarOffset.x}px), ${toolbarOffset.y}px)`,
          zIndex: 20,
          pointerEvents: "none",
        }}
      >
        <Paper
          elevation={6}
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            pointerEvents: "auto",
            cursor: "grab",
          }}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            if (event.target.closest("button")) return;
            toolbarDragRef.current = {
              startX: event.clientX,
              startY: event.clientY,
              baseX: toolbarOffset.x,
              baseY: toolbarOffset.y,
            };
            document.body.style.cursor = "grabbing";
          }}
        >
          {/* Thumbnail Toggle */}
          <Tooltip
            title={showThumbnails ? "Hide Thumbnails" : "Show Thumbnails"}
          >
            <IconButton
              size="small"
              onClick={() => setShowThumbnails(!showThumbnails)}
              color={showThumbnails ? "primary" : "default"}
            >
              <ViewSidebarIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Annotation numbers and tooltips toggle (all modes) */}
          <Tooltip
            title={showAnnotationLabels ? "Hide annotation numbers and tooltips" : "Show annotation numbers and tooltips"}
          >
            <IconButton
              size="small"
              onClick={() => setShowAnnotationLabels((v) => !v)}
              color={showAnnotationLabels ? "primary" : "default"}
              aria-label={showAnnotationLabels ? "Hide labels" : "Show labels"}
            >
              <LabelIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* PDF Tint Toggle (Dark Mode) */}
          {isDarkMode && (
            <Tooltip
              title={tintEnabled ? "Disable PDF Tint" : "Enable PDF Tint"}
            >
              <IconButton
                size="small"
                onClick={() => setTintEnabled(!tintEnabled)}
                color={tintEnabled ? "primary" : "default"}
              >
                <DarkModeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Divider orientation="vertical" flexItem />

          {mode === "annotator" && (
            <Tooltip
              title={
                signaturePlacementMode || initialsPlacementMode
                  ? "Switch to annotate (draw fields)"
                  : "Annotate (draw new fields)"
              }
            >
              <IconButton
                size="small"
                color={!signaturePlacementMode && !initialsPlacementMode ? "primary" : "default"}
                aria-label="Annotate"
                onClick={
                  signaturePlacementMode && onToggleSignaturePlacement
                    ? () => onToggleSignaturePlacement()
                    : initialsPlacementMode && onToggleInitialsPlacement
                      ? () => onToggleInitialsPlacement()
                      : undefined
                }
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {mode === "json" && onToggleIdentifySections && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={
                signaturePlacementMode || initialsPlacementMode
                  ? null
                  : identifySectionsMode
                  ? "highlight"
                  : "annotate"
              }
              onChange={(_, value) => {
                if (!value) return;
                if (value === "annotate" && signaturePlacementMode && onToggleSignaturePlacement) {
                  onToggleSignaturePlacement();
                }
                if (value === "annotate" && initialsPlacementMode && onToggleInitialsPlacement) {
                  onToggleInitialsPlacement();
                }
                onToggleIdentifySections(value === "highlight");
              }}
            >
              <Tooltip title="Annotate (draw to create or multi-select)">
                <ToggleButton value="annotate" aria-label="Annotate">
                  <EditIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Highlight (drag to select fields only)">
                <ToggleButton value="highlight" aria-label="Highlight">
                  <CropFreeIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          )}
          {(mode === "annotator" || mode === "json") && onToggleSignaturePlacement && (
            <Tooltip title="Add Signature">
              <IconButton
                size="small"
                onClick={onToggleSignaturePlacement}
                color={signaturePlacementMode ? "primary" : "default"}
              >
                <BorderColorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {(mode === "annotator" || mode === "json") && onToggleInitialsPlacement && (
            <Tooltip title="Add Initials">
              <IconButton
                size="small"
                onClick={onToggleInitialsPlacement}
                color={initialsPlacementMode ? "primary" : "default"}
                aria-label="Add Initials"
              >
                <ShortTextIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {mode === "json" && onLineHeightSet && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                  Line height:
                </Typography>
                {lineHeightSettingMode ? (
                  <>
                    <Button size="small" variant="contained" color="primary" onClick={confirmLineHeightFromBox}>
                      Confirm
                    </Button>
                    <Button size="small" onClick={onLineHeightCancel}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", lineHeight: 0 }}>
                        {defaultLineHeightPx != null && onLineHeightConfirm ? (
                          <Tooltip title="Increase line height">
                            <IconButton
                              size="small"
                              sx={{ p: 0, minWidth: 20, minHeight: 14 }}
                              onClick={() => onLineHeightConfirm(defaultLineHeightPx + 1)}
                            >
                              <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            minWidth: 44,
                            px: 0.5,
                            fontFamily: "monospace",
                            opacity: defaultLineHeightPx != null ? 1 : 0.6,
                          }}
                        >
                          {defaultLineHeightPx != null ? `${defaultLineHeightPx} px` : "—"}
                        </Typography>
                        {defaultLineHeightPx != null && onLineHeightConfirm ? (
                          <Tooltip title="Decrease line height">
                            <IconButton
                              size="small"
                              sx={{ p: 0, minWidth: 20, minHeight: 14 }}
                              onClick={() => onLineHeightConfirm(Math.max(1, defaultLineHeightPx - 1))}
                            >
                              <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </Box>
                    <Button size="small" variant="outlined" onClick={onLineHeightSet}>
                      Set
                    </Button>
                    {defaultLineHeightPx != null && onLineHeightReset && (
                      <Button size="small" onClick={onLineHeightReset} color="inherit">
                        Reset
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </>
          )}

          {(mode === "annotator" || mode === "json") && (
            <>
              <Divider orientation="vertical" flexItem />
              <Tooltip title={textHighlightMode ? "Exit text highlight (select PDF text)" : "Text highlight (I-beam cursor, select text from PDF)"}>
                <IconButton
                  size="small"
                  onClick={() => setTextHighlightMode((prev) => !prev)}
                  color={textHighlightMode ? "primary" : "default"}
                  aria-label={textHighlightMode ? "Exit text highlight" : "Text highlight"}
                >
                  <TextHighlightIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {((selectedSignatureId && onSignatureLocationChange && selectedSignatureLoc) || (selectedNudgeTarget && (onFieldLocationChange || onOptionLocationChange || (selectedNudgeTarget.type === "additional" && onAdditionalFieldLocationChange) || (selectedNudgeTarget.type === "subsequentSigning" && onSubsequentSigningLocationChange) || (selectedNudgeTarget.type === "signerBinding" && onSignerBindingLocationChange) || (selectedNudgeTarget.type === "signatureAnnotation" && onSignatureLocationChange)))) && !textHighlightMode && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" sx={{ mr: 0.5, minWidth: 40 }}>Nudge:</Typography>
                  <Tooltip title="Left 1px (←)"><IconButton size="small" onClick={() => handleNudgeResize(-1, 0, 0, 0)} sx={{ minWidth: 28 }} aria-label="Nudge left">←</IconButton></Tooltip>
                  <Tooltip title="Right 1px (→)"><IconButton size="small" onClick={() => handleNudgeResize(1, 0, 0, 0)} sx={{ minWidth: 28 }} aria-label="Nudge right">→</IconButton></Tooltip>
                  <Tooltip title="Up 1px (↑)"><IconButton size="small" onClick={() => handleNudgeResize(0, -1, 0, 0)} sx={{ minWidth: 28 }} aria-label="Nudge up">↑</IconButton></Tooltip>
                  <Tooltip title="Down 1px (↓)"><IconButton size="small" onClick={() => handleNudgeResize(0, 1, 0, 0)} sx={{ minWidth: 28 }} aria-label="Nudge down">↓</IconButton></Tooltip>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" sx={{ mr: 0.5, minWidth: 40 }}>Resize:</Typography>
                  <Tooltip title="Width -1px (Alt+←)"><IconButton size="small" onClick={() => handleNudgeResize(0, 0, -1, 0)} sx={{ minWidth: 28 }}>W−</IconButton></Tooltip>
                  <Tooltip title="Width +1px (Alt+→)"><IconButton size="small" onClick={() => handleNudgeResize(0, 0, 1, 0)} sx={{ minWidth: 28 }}>W+</IconButton></Tooltip>
                  <Tooltip title="Height -1px (Alt+↑)"><IconButton size="small" onClick={() => handleNudgeResize(0, 0, 0, -1)} sx={{ minWidth: 28 }}>H-</IconButton></Tooltip>
                  <Tooltip title="Height +1px (Alt+↓)"><IconButton size="small" onClick={() => handleNudgeResize(0, 0, 0, 1)} sx={{ minWidth: 28 }}>H+</IconButton></Tooltip>
                </Box>
              </Box>
            </>
          )}

          {/* Zoom Controls */}
          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="Zoom Out">
              <IconButton
                size="small"
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Reset Zoom (${Math.round(scale * 100)}%)`}>
              <IconButton size="small" onClick={handleResetZoom}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom In">
              <IconButton
                size="small"
                onClick={handleZoomIn}
                disabled={scale >= 3.0}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ButtonGroup>

          {onReplacePdf && (
            <Tooltip title="Replace PDF (keeps annotations)">
              <IconButton size="small" onClick={onReplacePdf} aria-label="Replace PDF">
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Close PDF">
            <IconButton size="small" onClick={onClose} aria-label="Close PDF">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      </Box>
    </Box>
  );
};

export default InteractivePdfViewer;
